/**
 * commands.ts — full command registry + handlers.
 *
 * Phase CG. Every command the CLI exposes lives here. Each handler is
 * passed a Ctx with {feed, args, slug, identity} and either appends a DOM
 * card or prints a coached error.
 *
 * Locked vocabulary applies — durations are Zeqonds (Z), never seconds.
 */

import { ZeqondClock } from "../orb/zeqond-clock.js";
import { getSlug } from "../agent/agent-rail-bridge.js";
import { Api, getIdentity } from "./api-client.js";
import {
  card, ckoCard, line, table, esc, closest, fmtZ,
  type Category, type ComputeResponse,
} from "./formatters.js";
import { GROUPS, renderHelp, renderDeepHelp, commandNames, findCommand } from "./help.js";
import { renderWelcome, renderTutorialStep, TUTORIAL_STEPS, welcomeProbe } from "./welcome.js";
import {
  API_GROUPS, API_TOTAL_ENDPOINTS, API_TOTAL_GROUPS,
  PROTOCOL_THEMES, PROTOCOL_DOMAINS, PROTOCOL_TOTAL,
  OPERATOR_FAMILIES, OPERATOR_TOTAL, OPERATOR_DOMAINS,
  KERNEL_ENDPOINTS, KERNEL_INSTALLERS,
  DOC_MAP,
} from "./sdk-surface.js";
import { renderLearnMore } from "./learn-more.js";

/**
 * BUILD-A — the programmable contract. Every command run produces one
 * CommandResult. The same object backs three consumers:
 *   • the interactive web-cli (renders cards, ignores the struct)
 *   • `--json` / machine mode (prints `data` as JSON)
 *   • the headless runner `runHeadless()` (returns the struct + exit code)
 * exitCode convention: 0 ok · 1 error · 2 usage · 127 unknown command.
 */
export interface CommandResult {
  ok: boolean;
  exitCode: number;
  command: string;
  data?: unknown;
  error?: string;
}

export interface Ctx {
  feed: HTMLElement;
  append: (el: HTMLElement) => void;
  print: (text: string, kind?: "user" | "ok" | "err" | "dim") => void;
  args: string[];
  raw: string;
  /** Mutable runtime — tutorial step, theme, json-mode, etc. */
  state: Runtime;
  /** True when this invocation should emit machine-readable JSON. */
  json: boolean;
  /** Structured result for this invocation (headless/programmatic reads). */
  result: CommandResult;
  /**
   * Emit a result. In card mode → renders the liquid-glass card; in json
   * mode → prints `data` as JSON. Always records `data` on `result` so the
   * headless runner / an embedding app gets the machine-readable payload.
   */
  respond: (category: Category, title: string, body: string, footer?: string, data?: unknown) => void;
  /** Emit a failure: prints (or JSON-errors), sets ok=false + exitCode. */
  fail: (message: string, exitCode?: number, data?: unknown) => void;
}

export interface Runtime {
  tutorialStep: number;
  tutorialActive: boolean;
  theme: string;
  /** Persistent JSON output mode (toggled by the `json` command). */
  json: boolean;
}

export const runtime: Runtime = {
  tutorialStep: 0,
  tutorialActive: false,
  theme: "cryptopunk",
  json: false,
};

/**
 * Build a fully-wired Ctx from a minimal base. Shared by the DOM runner
 * (web-cli.ts runCommand) and the headless runner (runHeadless) so the
 * respond/fail/result contract is defined in exactly one place.
 */
export function makeCtx(base: {
  feed: HTMLElement;
  append: (el: HTMLElement) => void;
  print: (text: string, kind?: "user" | "ok" | "err" | "dim") => void;
  args: string[];
  raw: string;
  json?: boolean;
}): Ctx {
  const command = (base.raw.trim().split(/\s+/)[0] || "").toLowerCase();
  const json = base.json ?? false;
  const result: CommandResult = { ok: true, exitCode: 0, command };
  return {
    feed: base.feed,
    append: base.append,
    print: base.print,
    args: base.args,
    raw: base.raw,
    state: runtime,
    json,
    result,
    respond(category, title, body, footer, data) {
      result.ok = true;
      if (data !== undefined) result.data = data;
      if (json) base.print(JSON.stringify({ ok: true, command, data: data ?? null }, null, 2), "ok");
      else base.append(card(category, title, body, footer));
    },
    fail(message, exitCode = 1, data) {
      result.ok = false;
      result.exitCode = exitCode;
      result.error = message;
      if (data !== undefined) result.data = data;
      if (json) base.print(JSON.stringify({ ok: false, command, error: message }, null, 2), "err");
      else base.print(message, "err");
    },
  };
}

// ── Command map ─────────────────────────────────────────────────────────

type Handler = (ctx: Ctx) => Promise<void> | void;
const REGISTRY: Record<string, Handler> = {};
export function register(name: string, fn: Handler): void { REGISTRY[name] = fn; }

/** Dispatch the head command. Returns true if handled, false if unknown. */
export async function dispatch(ctx: Ctx, head: string): Promise<boolean> {
  const fn = REGISTRY[head.toLowerCase()];
  if (!fn) return false;
  try {
    await fn(ctx);
  } catch (err) {
    ctx.fail("error: " + ((err as Error)?.message || String(err)), 1);
  }
  return true;
}

// ── json — persistent machine-readable output toggle ────────────────────
register("json", (ctx) => {
  const sub = (ctx.args[0] || "status").toLowerCase();
  if (sub === "on" || sub === "true")  { runtime.json = true;  ctx.print("json output ON — commands now emit machine-readable JSON. `json off` to restore cards.", "ok"); return; }
  if (sub === "off" || sub === "false"){ runtime.json = false; ctx.print("json output OFF — interactive cards restored.", "ok"); return; }
  ctx.print("json output is " + (runtime.json ? "ON" : "OFF") + " · use `json on` / `json off` · or append `--json` to any command.", "dim");
});

// ── Onboarding / meta ───────────────────────────────────────────────────

register("help", (ctx) => {
  if (ctx.args.length === 0) {
    ctx.append(renderHelp());
    return;
  }
  const target = ctx.args[0];
  const el = renderDeepHelp(target);
  if (el) ctx.append(el);
  else {
    const c = closest(target, commandNames());
    ctx.print('no help for "' + target + '"' + (c ? ' — did you mean `' + c + '`?' : ''), "err");
  }
});

register("examples", (ctx) => {
  const exs: string[] = [];
  for (const g of GROUPS) for (const c of g.cmds) if (c.example) exs.push(c.example);
  const html = exs.slice(0, 24).map(e => '<div class="cli-ex-row"><code>' + esc(e) + '</code></div>').join("");
  ctx.append(card("tutorial", "examples · " + exs.length + " runnable", html,
    '→ copy any line, paste at the prompt, hit Enter'));
});

register("docs", (ctx) => {
  const topic = (ctx.args[0] || "").toLowerCase();
  if (!topic) {
    // No topic → print the full catalogue card so the user sees what's available.
    const keys = Object.keys(DOC_MAP).sort();
    ctx.append(card("tutorial", "docs · " + keys.length + " topics",
      keys.map(k => '<div class="cli-cko-row"><span class="cli-cko-k"><code>docs ' + esc(k) + '</code></span><span class="cli-cko-v dim">' + esc(DOC_MAP[k]) + '</span></div>').join(""),
      '→ <code>docs &lt;topic&gt;</code> opens the SDK page in a new tab'));
    return;
  }
  const url = DOC_MAP[topic] ?? "/sdk/?q=" + encodeURIComponent(topic);
  if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
  ctx.print("opened " + url + " in a new tab.", "dim");
});

// ── Row-12 / S5 (2026-06-12): zeq verify — the flagship receipt check ──────
// Verifies a signed CKO/prove envelope with TWO independent legs:
//   1. Ed25519 signature of the §3 claim against this origin's node key
//      (crypto.subtle — works in the terminal AND both workbenches).
//   2. Cross-node recompute via POST /api/attest (the Phase-F verifier
//      mesh) — an INDEPENDENT node re-runs the math and bit-compares.
// Input: a file path (terminal), `-` for stdin, or pasted JSON (web).
function cliCanonicalJson(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number" || typeof v === "string") return JSON.stringify(v);
  if (Array.isArray(v)) return "[" + v.map(cliCanonicalJson).join(",") + "]";
  const o = v as Record<string, unknown>;
  return "{" + Object.keys(o).sort().map((k) => JSON.stringify(k) + ":" + cliCanonicalJson(o[k])).join(",") + "}";
}

register("verify", async (ctx) => {
  let raw = (ctx.args.join(" ") || "").trim();
  // Terminal: a path or `-` (stdin) — the node entry pre-reads files into
  // ctx.stdinFile when the first arg resolves to a readable file.
  const pre = (globalThis as Record<string, unknown>).__zeqVerifyFileText as string | undefined;
  if (pre) { raw = pre; delete (globalThis as Record<string, unknown>).__zeqVerifyFileText; }
  if (!raw) { ctx.fail("usage: verify <envelope.json | -> · paste the signed envelope from `compute --json` / prove", 2); return; }
  let env: any;
  try { env = JSON.parse(raw); } catch { ctx.fail("verify: input is not valid JSON", 2); return; }
  const signed = env.signed ?? env;
  if (!signed?.claim || !signed?.signature) {
    ctx.fail("verify: no signed claim found — run the compute with `--json` on a node with Phase-D signing (the envelope needs `signed.claim` + `signed.signature`)", 1);
    return;
  }
  const lines: string[] = [];
  // Leg 1 — Ed25519 against this origin's published node key.
  let sigVerdict = "unavailable";
  try {
    const idRes = await Api.raw("GET", "/api/identity");
    const pubHex = (idRes.data as any)?.public_key ?? (idRes.data as any)?.identity?.public_key;
    if (idRes.ok && typeof pubHex === "string" && /^[0-9a-f]{64}$/i.test(pubHex)) {
      const subtle = (globalThis as any).crypto?.subtle;
      if (subtle) {
        const keyBytes = new Uint8Array(pubHex.match(/.{2}/g)!.map((b: string) => parseInt(b, 16)));
        const sigBytes = new Uint8Array((signed.signature.match(/.{2}/g) || []).map((b: string) => parseInt(b, 16)));
        const payload = new TextEncoder().encode(cliCanonicalJson(signed.claim));
        const key = await subtle.importKey("raw", keyBytes, { name: "Ed25519" }, false, ["verify"]);
        const ok = await subtle.verify({ name: "Ed25519" }, key, sigBytes, payload);
        sigVerdict = ok ? "VERIFIED" : "SIGNATURE MISMATCH";
      }
    } else {
      sigVerdict = "node identity unavailable (" + (idRes.status || "?") + ")";
    }
  } catch (e) {
    sigVerdict = "signature check unavailable here (" + ((e as Error)?.message || "Ed25519 unsupported") + ")";
  }
  lines.push('<div class="cli-cko-row"><span class="cli-cko-k">Ed25519 signature</span><span class="cli-cko-v">' + esc(sigVerdict) + "</span></div>");
  // Leg 2 — independent recompute via the verifier mesh. The endpoint
  // wants the full signed block: { claim, signature, origin_public_key }.
  let attestVerdict = "skipped";
  try {
    let originPub = (signed as { origin_public_key?: string }).origin_public_key;
    if (!originPub) {
      const idRes2 = await Api.raw("GET", "/api/identity");
      originPub = (idRes2.data as any)?.public_key ?? (idRes2.data as any)?.identity?.public_key;
    }
    const at = await Api.raw("POST", "/api/attest", { claim: signed.claim, signature: signed.signature, origin_public_key: originPub });
    const verdict = (at.data as any)?.verdict ?? (at.data as any)?.attestation?.verdict;
    attestVerdict = at.ok ? String(verdict ?? JSON.stringify(at.data).slice(0, 60)) : "attest failed: " + (at.error || at.status);
  } catch (e) {
    attestVerdict = "attest unreachable (" + ((e as Error)?.message || "network") + ")";
  }
  lines.push('<div class="cli-cko-row"><span class="cli-cko-k">independent recompute</span><span class="cli-cko-v">' + esc(attestVerdict) + "</span></div>");
  const c = signed.claim as Record<string, unknown>;
  lines.push('<div class="cli-cko-row"><span class="cli-cko-k">claim</span><span class="cli-cko-v"><code>' + esc(String(c.value)) + " " + esc(String(c.unit ?? "")) + "</code> · Zeqond " + esc(String(c.zeqond ?? "—")) + "</span></div>");
  const pass = sigVerdict === "VERIFIED";
  ctx.result.data = { signature: sigVerdict, attest: attestVerdict, claim: c };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), pass ? "ok" : "err"); return; }
  ctx.append(card("system", "verify · " + (pass ? "RECEIPT VERIFIED" : "NOT VERIFIED"), lines.join(""),
    pass ? "the math is provable — anyone can re-run this check with zero secrets"
         : "do not trust this receipt until both legs pass"));
});

register("update", async (ctx) => {
  // Self-update: re-fetch /cli/zeq from the configured origin. The node
  // entry performs the actual file swap; in the browser this just reports.
  // Row 12.d fix: `typeof window` is shimmed in native too — the honest
  // discriminator is __ZEQ_NATIVE__ (the `update` regression doctor caught).
  if (!(globalThis as unknown as { __ZEQ_NATIVE__?: boolean }).__ZEQ_NATIVE__) {
    ctx.print("update applies to the installed terminal CLI — in the workbench you are always on the deployed version.", "dim");
    return;
  }
  const hook = (globalThis as unknown as { __zeqSelfUpdate?: () => Promise<string> }).__zeqSelfUpdate;
  if (!hook) { ctx.fail("update: self-update hook unavailable in this build", 1); return; }
  try {
    const msg = await hook();
    ctx.print(msg, "ok");
  } catch (e) {
    ctx.fail("update failed: " + ((e as Error)?.message || String(e)), 1);
  }
});

register("version", (ctx) => {
  const tick = ZeqondClock.now();
  ctx.append(card(
    "system",
    "Zeq Root — the CLI · v1.287.7 (Row 12.b)",
    [
      '<div class="cli-cko-row"><span class="cli-cko-k">framework</span><span class="cli-cko-v">ZeqVM · v1.287.5 kernel</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">clock</span><span class="cli-cko-v">HulyaPulse 1.287 Hz · τ = 0.777 s (1 Zeqond)</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">tolerance</span><span class="cli-cko-v">≤0.1% (KO42 metric tensioner)</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">operators</span><span class="cli-cko-v">1,500+ kernel · 42+ Kinematic surface · 65 domains</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">α (runtime)</span><span class="cli-cko-v">10⁻³ exactly <span class="dim">· α_econ = 1.29 × 10⁻³ (economy — separate)</span></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">FIXED_DT</span><span class="cli-cko-v">τ / 60 ≈ 12.95 mz</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">cache-bust</span><span class="cli-cko-v"><code>wb-DB-clifull</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">Zeqond</span><span class="cli-cko-v"><code>' + tick.zeqond + '</code> · phase ' + tick.phase.toFixed(6) + '</span></div>',
    ].join(""),
    'CLAUDE.md §1 — never round, never improvise',
  ));
});

register("clear", (ctx) => { ctx.feed.innerHTML = ""; });

register("history", (ctx) => {
  try {
    const raw = localStorage.getItem("zeq.cli.history");
    const items = raw ? JSON.parse(raw) as string[] : [];
    if (ctx.args[0] === "clear") {
      localStorage.removeItem("zeq.cli.history");
      ctx.print("history cleared.", "dim");
      return;
    }
    if (!items.length) { ctx.print("(no history)", "dim"); return; }
    const html = items.slice(-30).reverse().map((c, i) =>
      '<div class="cli-ex-row"><span class="dim">' + (items.length - i) + '</span> <code>' + esc(c) + '</code></div>'
    ).join("");
    ctx.append(card("system", "history · last " + Math.min(30, items.length), html,
      'use ↑/↓ to recall · <code>history clear</code> to wipe'));
  } catch { ctx.print("history unavailable.", "err"); }
});

register("exit", () => {
  // Re-dispatch via global hook
  if (typeof window !== "undefined" && (window as { zeqCli?: { close?: () => void } }).zeqCli) {
    (window as { zeqCli: { close: () => void } }).zeqCli.close();
  }
});
register("close", REGISTRY["exit"]);
register("open", (ctx) => { ctx.print("CLI is already open.", "dim"); });

// ── Hello / hello universe ──────────────────────────────────────────────

register("hello", async (ctx) => {
  if (ctx.args[0]?.toLowerCase() === "universe") return helloUniverse(ctx);
  ctx.print('running compute NM19 (Newton\'s 2nd law) with mass=5, acceleration=2 …', "dim");
  const r = await Api.compute(["NM19"], { mass: 5, acceleration: 2 }, "Newtonian Mechanics");
  if (!r.ok) {
    ctx.print("compute failed: " + (r.error || ("HTTP " + r.status)), "err");
    if (r.status === 401) ctx.print("→ try: sign in at /apps/zeq-byok/ first, then re-run `hello`.", "dim");
    return;
  }
  ctx.append(ckoCard("NM19 · F = m × a", r.data as ComputeResponse));
  ctx.print('→ next: try `hello universe` for a 3-operator cross-domain run, or `operators count`.', "dim");
});

async function helloUniverse(ctx: Ctx): Promise<void> {
  ctx.print('hello universe · running 3 operators across QM / NM / GR …', "dim");
  const calls = [
    { label: "QM5 · ψ(x,t) Schrödinger", ops: ["QM5"], inputs: { mass: 9.10938356e-31, k: 1e10, x: 0.001, t: 0 }, domain: "Quantum Mechanics" },
    { label: "NM21 · F = G m₁ m₂ / r²",  ops: ["NM21"], inputs: { mass1: 5.972e24, mass2: 7.342e22, distance: 3.84e8 }, domain: "Newtonian Mechanics" },
    { label: "GR37 · Schwarzschild r_s", ops: ["GR37"], inputs: { mass: 1.989e30 }, domain: "General Relativity" },
  ];
  for (const c of calls) {
    const r = await Api.compute(c.ops, c.inputs, c.domain);
    if (r.ok) {
      ctx.append(ckoCard(c.label, r.data as ComputeResponse));
    } else {
      ctx.append(card("compute", c.label + ' · <span class="dim">failed</span>',
        '<div class="dim">' + esc(r.error || ("HTTP " + r.status)) + '</div>',
        'this operator may need different inputs · try `operators show ' + (c.ops[0]) + '`'));
    }
  }
  ctx.print('→ each row is a signed CKO envelope · same KO42 carrier · same precision gate · reproducible against NIST CODATA 2018.', "dim");
}

// ── Tutorial ────────────────────────────────────────────────────────────

// Row 12.b — progress persists in localStorage (file-backed ~/.zeqrc in the
// native CLI), so one-shot `zeq tutorial` invocations resume where you left
// off across fresh processes. In-session (REPL/workbench), bare `tutorial`
// advances; cross-session it RESUMES at the saved step.
const TUTORIAL_KEY = "zeq.cli.tutorial.step";
function tutorialSaved(): number {
  try { return Math.max(0, Math.min(TUTORIAL_STEPS, parseInt(localStorage.getItem(TUTORIAL_KEY) || "0", 10) || 0)); }
  catch { return 0; }
}
function tutorialPersist(step: number): void {
  try { localStorage.setItem(TUTORIAL_KEY, String(step)); } catch { /* no storage */ }
}

register("tutorial", async (ctx) => {
  const sub = (ctx.args[0] || "").toLowerCase();
  if (sub === "exit") { ctx.state.tutorialActive = false; ctx.state.tutorialStep = 0; tutorialPersist(0); ctx.print("tutorial closed — progress cleared. `tutorial` starts fresh.", "dim"); return; }
  const saved = tutorialSaved();
  // Rehydrate cross-process state (native one-shot runs start cold).
  if (!ctx.state.tutorialActive && saved > 0) ctx.state.tutorialStep = saved;
  if (sub === "reset") { ctx.state.tutorialStep = 1; }
  else if (/^[1-9]$/.test(sub)) { ctx.state.tutorialStep = Math.min(TUTORIAL_STEPS, parseInt(sub, 10)); }
  else if (sub === "back") { ctx.state.tutorialStep = Math.max(1, ctx.state.tutorialStep - 1); }
  else if (sub === "next" || (sub === "" && ctx.state.tutorialActive)) {
    ctx.state.tutorialStep = Math.min(TUTORIAL_STEPS, ctx.state.tutorialStep + 1);
  } else if (sub === "" && saved > 0 && saved < TUTORIAL_STEPS) {
    // resuming from a previous session — show the saved step, don't advance
    ctx.state.tutorialStep = saved;
    if (!ctx.json) ctx.print("resuming tutorial at step " + saved + " / " + TUTORIAL_STEPS + " · `tutorial reset` to start over", "dim");
  } else {
    ctx.state.tutorialStep = 1;
  }
  ctx.state.tutorialActive = true;
  tutorialPersist(ctx.state.tutorialStep);
  const el = await renderTutorialStep(ctx.state.tutorialStep);
  ctx.append(el);
  ctx.result.data = { step: ctx.state.tutorialStep, of: TUTORIAL_STEPS };
  if (ctx.state.tutorialStep >= TUTORIAL_STEPS) ctx.state.tutorialActive = false;
});

// ── Identity & wallet ───────────────────────────────────────────────────

register("whoami", async (ctx) => {
  const id = getIdentity();
  if (!id.slug && !id.zid) {
    ctx.append(card("identity", "whoami · <span class=\"dim\">guest</span>",
      '<div class="dim">not signed in · open <code>/apps/zeq-byok/</code> or the homepage wizard to register.</div>',
      'guest tier: free Fireworks chat · no credits · no machine'));
    return;
  }
  let balance: string | null = null;
  if (id.zid) {
    const r = await Api.accountBalance(id.zid);
    if (r.ok && r.data) {
      const d = r.data as { balance?: number };
      if (typeof d.balance === "number") balance = d.balance.toFixed(3) + " ZEQ";
    }
  }
  ctx.respond("identity", "whoami · " + esc(id.slug ?? "(no machine)"), [
    '<div class="cli-cko-row"><span class="cli-cko-k">ZID</span><span class="cli-cko-v"><code>' + esc(id.zid ?? "—") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">machine</span><span class="cli-cko-v"><code>' + esc(id.slug ?? "—") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">tier</span><span class="cli-cko-v"><code>' + esc(id.tier ?? "starter") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">email</span><span class="cli-cko-v"><code>' + esc(id.email ?? "—") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">balance</span><span class="cli-cko-v"><code>' + esc(balance ?? "(querying…)") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">api key</span><span class="cli-cko-v"><code>' + esc(id.apiKey ? id.apiKey.slice(0, 12) + "…" : "—") + '</code></span></div>',
  ].join(""), '→ try: <code>credits</code> · <code>pulse</code> · <code>operators count</code>',
    { zid: id.zid, slug: id.slug, tier: id.tier, email: id.email, balance });
});

register("signout", (ctx) => {
  if (typeof window !== "undefined") {
    try { localStorage.removeItem("zeq.identity.snapshot"); } catch { /* */ }
    try { document.cookie = "zeqsid=; Max-Age=0; path=/"; } catch { /* */ }
    ctx.print("local session cleared · reload to complete sign-out.", "ok");
  }
});

register("credits", async (ctx) => {
  const sub = (ctx.args[0] || "").toLowerCase();
  const id = getIdentity();
  const slug = id.slug;
  if (!slug) { ctx.print("no machine — register one first.", "err"); return; }

  if (sub === "swap") {
    const which = (ctx.args[1] || "").toLowerCase();
    const amount = parseFloat(ctx.args[2] || "");
    if ((which !== "btc" && which !== "eth") || !isFinite(amount)) {
      ctx.print("usage: credits swap <btc|eth> <amount>", "err");
      return;
    }
    ctx.print("→ /tally/?slug=" + slug + " · use the Swap panel to complete in browser (signed flow).", "dim");
    if (typeof window !== "undefined") window.open("/tally/?slug=" + encodeURIComponent(slug), "_blank", "noopener");
    return;
  }
  if (sub === "tally") {
    if (typeof window !== "undefined") window.open("/tally/?slug=" + encodeURIComponent(slug), "_blank", "noopener");
    ctx.print("opened /tally/?slug=" + slug, "dim");
    return;
  }

  const zid = id.zid;
  if (!zid) { ctx.print("no identity — sign in first.", "err"); return; }
  const r = await Api.creditsBalance(zid);
  if (!r.ok) { ctx.fail("credits fetch failed: " + (r.error || "HTTP " + r.status), 1); return; }
  const d = (r.data || {}) as Record<string, unknown>;
  // Envelope economy (P19-A2): credits are immovable FUEL; ZEQ envelopes are
  // the only transferable asset.
  const fuel    = (d.free_credits as number | undefined);
  const zeq     = (d.paid_credits as number | undefined) ?? (d.balance as number | undefined);
  const env     = (d.envelopes as { count?: number; your_share_total?: number } | undefined) || {};
  const minted  = d.tokens_minted as number | undefined;
  const burned  = d.tokens_burned as number | undefined;
  const daily   = d.daily_rate as number | undefined;
  const tier    = (d.tier as string | undefined) ?? id.tier ?? "free";
  ctx.respond("wallet", "credits · " + esc(slug), [
    '<div class="cli-cko-row"><span class="cli-cko-k">credits (fuel)</span><span class="cli-cko-v"><code>' + esc(typeof fuel === "number" ? fuel.toLocaleString() : "—") + '</code> <span class="dim">immovable · spent on compute</span></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">ZEQ balance</span><span class="cli-cko-v"><code>' + esc(typeof zeq === "number" ? zeq.toFixed(3) + " ZEQ" : "—") + '</code> <span class="dim">transferable</span></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">envelopes</span><span class="cli-cko-v"><code>' + esc(typeof env.count === "number" ? String(env.count) : "0") + '</code>' + (typeof env.your_share_total === "number" && env.your_share_total > 0 ? ' <span class="dim">· share ' + esc(env.your_share_total.toFixed(3)) + '</span>' : '') + '</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">tier</span><span class="cli-cko-v"><code>' + esc(tier) + '</code>' + (typeof daily === "number" ? ' <span class="dim">· ' + esc(daily.toLocaleString()) + ' credits/day claim</span>' : '') + '</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">minted / burned</span><span class="cli-cko-v"><code>' + esc(typeof minted === "number" ? minted.toFixed(3) : "—") + '</code> / <code>' + esc(typeof burned === "number" ? burned.toFixed(3) : "—") + '</code></span></div>',
  ].join(""), '→ <code>credits swap btc 0.001</code> · <code>credits tally</code> for full viewer', d);
});
register("wallet", REGISTRY["credits"]); // back-compat alias (hidden from help/autocomplete)

// ── Send a ZEQ envelope coin (P19-PUB Phase 2b) ─────────────────────────
// The ONLY transferable asset. `send` with no token lists your coins; with a
// token + recipient it reassigns whole-envelope ownership. PRIVATE by default
// — add `--public` to also publish the transfer's proof-of-compute receipt to
// the hosting domain's observer (the public explorer). Value moves either way.
register("send", async (ctx) => {
  const id = getIdentity();

  // command-specific flags: --public / --private. If NEITHER is given, leave
  // visibility undefined so the server applies this machine's publish-default
  // (Phase 2c). An explicit flag always wins.
  const wantPublic = ctx.args.includes("--public");
  const wantPrivate = ctx.args.includes("--private");
  const visibility: "public" | "private" | undefined = wantPublic ? "public" : wantPrivate ? "private" : undefined;
  const args = ctx.args.filter((a) => !a.startsWith("--"));
  const tokenId = args[0] || "";
  const to = (args[1] || "").toUpperCase();

  // ── `send default [public|private]` — view / set the per-machine publish
  //    default (Phase 2c). No value → show the current setting.
  if (tokenId.toLowerCase() === "default") {
    const v = (args[1] || "").toLowerCase();
    if (!v) {
      const r = await Api.publishDefaultGet();
      if (!r.ok) { ctx.fail("could not read default: " + (r.error || "HTTP " + r.status), 1); return; }
      const d = (r.data || {}) as { publish_economy_public?: boolean; slug?: string };
      ctx.result.data = d;
      if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
      ctx.append(card("wallet", "publish default · " + esc(d.slug || ""), '<div class="cli-cko-row"><span class="cli-cko-k">default</span><span class="cli-cko-v"><code>' + (d.publish_economy_public ? "public" : "private") + '</code> <span class="dim">' + (d.publish_economy_public ? "transfers publish to the explorer unless you pass --private" : "transfers stay private unless you pass --public") + '</span></span></div>', '→ <code>send default public</code> · <code>send default private</code>'));
      return;
    }
    if (v !== "public" && v !== "private") { ctx.fail("usage: send default <public|private>", 2); return; }
    const r = await Api.publishDefaultSet(v === "public");
    if (!r.ok) { ctx.fail("could not set default: " + (r.error || "HTTP " + r.status), 1); return; }
    const d = (r.data || {}) as { publish_economy_public?: boolean };
    ctx.result.data = d;
    if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
    ctx.print("publish default → " + (d.publish_economy_public ? "public" : "private"), "ok");
    return;
  }

  // ── `send claims` — list coins you hold that are HOMED on a peer chain (P4.3).
  if (tokenId.toLowerCase() === "claims") {
    const zid2 = id.zid;
    if (!zid2) { ctx.fail("listing claims needs your ZID — sign in.", 2); return; }
    const r = await Api.claimsList(zid2);
    if (!r.ok) { ctx.fail("could not load claims: " + (r.error || "HTTP " + r.status), 1); return; }
    const claims = ((r.data as { claims?: Array<{ token_id?: string; home_origin?: string; status?: string; spend_seq?: number|string }> })?.claims) || [];
    ctx.result.data = r.data;
    if (ctx.json) { ctx.print(JSON.stringify(r.data, null, 2), "ok"); return; }
    if (!claims.length) { ctx.append(card("wallet", "claims · none", '<div class="dim">no peer-homed coins held. When someone sends you a coin from another domain, import it to see it here.</div>')); return; }
    ctx.append(card("wallet", "claims · " + claims.length, claims.map((c) =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(String(c.token_id || "")) + '</code></span><span class="cli-cko-v"><span class="dim">home ' + esc(String(c.home_origin || "")) + ' · ' + esc(String(c.status || "")) + '</span></span></div>').join(""),
      '→ <code>send claim &lt;token_id&gt; &lt;ZID&gt; [to_origin]</code> to move one onward'));
    return;
  }

  // ── `send claim <token_id> <ZID> [to_origin]` — onward-transfer a held claim
  //    via the local proxy (server routes to the coin's home chain). P4.3c.
  if (tokenId.toLowerCase() === "claim") {
    // `send claim publish <token_id>` — anchor the claim's receipt to this
    // domain's observer (P4.4).
    if ((args[1] || "").toLowerCase() === "publish") {
      const ptok = args[2] || "";
      if (!/^ZT-/.test(ptok)) { ctx.fail("usage: send claim publish <token_id>", 2); return; }
      const r = await Api.claimPublish(ptok);
      if (!r.ok) { ctx.fail("publish failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
      const d = (r.data || {}) as { home_origin?: string; observer_slug?: string };
      ctx.result.data = d;
      if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
      ctx.append(card("wallet", "claim published to observer", '<div class="cli-cko-row"><span class="cli-cko-k">coin</span><span class="cli-cko-v"><code>' + esc(ptok) + '</code></span></div><div class="cli-cko-row"><span class="cli-cko-k">anchored to</span><span class="cli-cko-v"><code>' + esc(String(d.home_origin || "")) + '</code></span></div>', "now public on this domain's observer · verifiable against the home chain"));
      return;
    }
    const ctok = args[1] || "";
    const cto = (args[2] || "").toUpperCase();
    const ctoOrigin = args[3] || "";
    if (!/^ZT-/.test(ctok)) { ctx.fail("usage: send claim <token_id> <ZID> [to_origin]  ·  send claim publish <token_id>", 2); return; }
    if (!/^ZEQ(ORB)?\d+$/i.test(cto)) { ctx.fail("usage: send claim <token_id> <ZID> [to_origin]", 2); return; }
    const r = await Api.claimOnward(ctok, cto, ctoOrigin || undefined);
    if (!r.ok) { ctx.fail("onward transfer failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
    const d = (r.data || {}) as { home_origin?: string; spend_seq?: number; receipt?: string };
    ctx.result.data = d;
    if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
    ctx.append(card("wallet", "claim sent onward", [
      '<div class="cli-cko-row"><span class="cli-cko-k">coin</span><span class="cli-cko-v"><code>' + esc(ctok) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">to</span><span class="cli-cko-v"><code>' + esc(cto) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">home</span><span class="cli-cko-v"><code>' + esc(String(d.home_origin || "")) + '</code> <span class="dim">serialized at seq ' + esc(String(d.spend_seq ?? "?")) + '</span></span></div>',
    ].join(""), "the home chain serialized it; your local claim is now spent"));
    return;
  }

  // No token → list the envelopes you hold (discovery helper). This read is
  // per-ZID and owner-gated, so it needs to know your ZID — available from your
  // browser session. Headless (Bearer key only), skip the list and send
  // directly: `send <token_id> <ZID>` (the transfer derives the sender server-side).
  if (!tokenId) {
    const zid = id.zid;
    if (!zid) { ctx.fail("listing needs your ZID — sign in, or send directly: send <token_id> <ZID> [--public|--private]", 2); return; }
    const lr = await Api.envelopesList(zid);
    if (!lr.ok) { ctx.fail("could not load envelopes: " + (lr.error || "HTTP " + lr.status), 1); return; }
    const envs = ((lr.data as { envelopes?: Array<{ token_id?: string; user_share_zqt?: number }> })?.envelopes) || [];
    ctx.result.data = lr.data;
    if (ctx.json) { ctx.print(JSON.stringify(lr.data, null, 2), "ok"); return; }
    if (!envs.length) { ctx.append(card("wallet", "send · no envelopes yet", '<div class="dim">run a <code>compute</code> to mint a ZEQ envelope coin, then <code>send &lt;token_id&gt; &lt;ZID&gt;</code>.</div>')); return; }
    ctx.append(card("wallet", "your envelopes · " + envs.length, envs.map((e) =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(String(e.token_id || "")) + '</code></span><span class="cli-cko-v"><code>' + esc(Number(e.user_share_zqt || 0).toFixed(3)) + ' ZEQ</code></span></div>').join(""),
      '→ <code>send &lt;token_id&gt; &lt;ZID&gt;</code> · <code>--public</code>/<code>--private</code> override your default (<code>send default</code>)'));
    return;
  }

  if (!/^ZT-/.test(tokenId)) { ctx.fail("token_id must look like ZT-… — run `send` with no args to list yours.", 2); return; }
  if (!/^ZEQ(ORB)?\d+$/i.test(to)) { ctx.fail("usage: send <token_id> <recipient ZID> [--public|--private]", 2); return; }

  const r = await Api.envelopeTransfer(tokenId, to, visibility);
  if (!r.ok) { ctx.fail("transfer failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
  const d = (r.data || {}) as { token_id?: string; to?: string; coin_value?: number; sender_slug?: string; transfer_zeqond?: number; visibility?: string };
  ctx.result.data = d;
  if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
  const isPub = (d.visibility ?? visibility) === "public";
  const link = (d.sender_slug && d.transfer_zeqond) ? ("/state/detail.html?slug=" + d.sender_slug + "&zeqond=" + d.transfer_zeqond) : "";
  ctx.append(card("wallet", "envelope sent · " + esc(isPub ? "public" : "private"), [
    '<div class="cli-cko-row"><span class="cli-cko-k">coin</span><span class="cli-cko-v"><code>' + esc(d.token_id || tokenId) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">to</span><span class="cli-cko-v"><code>' + esc(d.to || to) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">visibility</span><span class="cli-cko-v"><code>' + esc(isPub ? "public" : "private") + '</code> <span class="dim">' + (isPub ? "published to the observer — anyone can verify" : "private — only you and the recipient") + '</span></span></div>',
  ].join(""), link ? ('→ receipt: <code>' + esc(link) + '</code>') : ""));
});
register("transfer", REGISTRY["send"]); // alias

// ── Developer API keys (BUILD-F) — headless / app auth ──────────────────
// Owner-invoked. `keys mint` returns the raw zeq_ak_ key ONCE, shown only in
// the owner's own terminal; it is never sent anywhere else. This is the
// credential an application built on the CLI uses to authenticate compute
// without a browser session: `Authorization: Bearer zeq_ak_…`.
register("keys", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.fail("no machine — sign in first.", 2); return; }

  if (sub === "mint" || sub === "create" || sub === "issue") {
    const name = ctx.args[1];
    const scope = (ctx.args[2] || "read").toLowerCase();
    if (!name) { ctx.fail('usage: keys mint <name> [read|write|admin]', 2); return; }
    if (!["read", "write", "admin"].includes(scope)) { ctx.fail("scope must be read | write | admin", 2); return; }
    const r = await Api.keysMint(slug, name, scope);
    if (!r.ok) { ctx.fail("mint failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
    const d = (r.data || {}) as { key?: string; api_key?: { id?: string; key_prefix?: string; scope?: string } };
    ctx.result.data = d; // includes the raw key — a headless caller (app) reads d.key
    if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
    ctx.append(card("identity", "API key minted · " + esc(name) + ' <span class="dim">· ' + esc(scope) + '</span>', [
      '<div class="cli-cko-row"><span class="cli-cko-k">key</span><span class="cli-cko-v"><code>' + esc(d.key ?? "—") + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">id</span><span class="cli-cko-v"><code>' + esc(d.api_key?.id ?? "—") + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">scope</span><span class="cli-cko-v"><code>' + esc(d.api_key?.scope ?? scope) + '</code></span></div>',
    ].join(""), '⚠ shown once — copy it now. Use as <code>Authorization: Bearer &lt;key&gt;</code> for /api/zeq/compute · revoke with <code>keys revoke ' + esc(d.api_key?.id ?? "<id>") + '</code>'));
    return;
  }

  if (sub === "revoke" || sub === "delete") {
    const id = ctx.args[1];
    if (!id) { ctx.fail("usage: keys revoke <id>", 2); return; }
    const r = await Api.keysRevoke(slug, id);
    if (!r.ok) { ctx.fail("revoke failed: " + (r.error || "HTTP " + r.status), 1); return; }
    ctx.result.data = { ok: true, revoked: id };
    if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
    ctx.print("revoked key " + id, "ok");
    return;
  }

  // default: list (no raw values)
  const r = await Api.keysList(slug);
  if (!r.ok) { ctx.fail("keys list failed: " + (r.error || "HTTP " + r.status), 1); return; }
  const d = (r.data || {}) as { api_keys?: Array<{ id: string; key_prefix?: string; name?: string; scope?: string; last_used_at?: string | null; revoked_at?: string | null }> };
  const list = d.api_keys || [];
  ctx.result.data = d;
  if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
  if (!list.length) { ctx.append(card("identity", "API keys · " + esc(slug), '<div class="dim">no keys yet — <code>keys mint &lt;name&gt; write</code> to create one for headless / app auth.</div>')); return; }
  ctx.append(card("identity", "API keys · " + list.length, list.map(k =>
    '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(k.key_prefix ?? "—") + '…</code></span><span class="cli-cko-v">' + esc(k.name ?? "—") + ' <span class="dim">· ' + esc(k.scope ?? "?") + (k.revoked_at ? ' · REVOKED' : (k.last_used_at ? ' · used' : ' · unused')) + ' · id ' + esc((k.id || "").slice(0, 8)) + '…</span></span></div>').join(""),
    '→ <code>keys mint &lt;name&gt; write</code> · <code>keys revoke &lt;id&gt;</code>'));
});

// ── Genesis — mint a new state machine (BUILD-I) ────────────────────────
// Browser-only (needs the HITE client crypto). Mirrors the homepage wizard's
// register-v3 flow: HITE-hash the equation client-side, then register. The
// equation IS the identity secret — the server only ever sees the hash. This
// mints a NEW ZID + auto state machine; it does NOT switch the active session.
async function ensureHiteCrypto(): Promise<{ randomSaltHex: () => string; hmacHite: (eq: string, salt: string) => Promise<string> } | null> {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  const w = window as unknown as { HiteCrypto?: { randomSaltHex: () => string; hmacHite: (eq: string, salt: string) => Promise<string> } };
  if (w.HiteCrypto) return w.HiteCrypto;
  await new Promise<void>((resolve) => {
    const s = document.createElement("script");
    s.src = "/js/hite-crypto.js";
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
  for (let i = 0; i < 25 && !w.HiteCrypto; i++) await new Promise((r) => setTimeout(r, 100));
  return w.HiteCrypto ?? null;
}

/**
 * Native CLI only: persist a freshly-minted identity as the active profile so
 * the very next `zeq` invocation (a fresh process) is authenticated as this
 * machine — `keys mint`, `credits`, `whoami`, `site deploy` work immediately.
 * Guarded by __ZEQ_NATIVE__ so the browser CLI (page session) is untouched.
 * Shared by `machine mint` and `signup` (Row 12.b).
 */
function persistActiveProfile(ctx: Ctx, d: { zid?: string; slug?: string; api_key?: string }): void {
  try {
    const gNative = (globalThis as unknown as { __ZEQ_NATIVE__?: boolean }).__ZEQ_NATIVE__;
    if (gNative && d.zid && d.api_key && typeof localStorage !== "undefined") {
      localStorage.setItem("zeq.cli.profile.key", d.api_key);
      localStorage.setItem("zeq.cli.profile.zid", String(d.zid));
      if (d.slug) localStorage.setItem("zeq.cli.profile.slug", String(d.slug));
      const originNow = (typeof location !== "undefined" ? location.origin : "");
      let store: { active: string | null; items: Record<string, unknown> } = { active: null, items: {} };
      try { const raw = localStorage.getItem("zeq.cli.profiles"); if (raw) store = JSON.parse(raw); } catch { /* */ }
      store.items["default"] = { key: d.api_key, zid: d.zid, slug: d.slug, origin: originNow };
      store.active = "default";
      localStorage.setItem("zeq.cli.profiles", JSON.stringify(store));
      if (!ctx.json) ctx.print("→ saved as active profile 'default' — you're now this machine (keys/credits/site work).", "dim");
    }
  } catch { /* non-native or no storage — skip */ }
}

register("machine", async (ctx) => {
  const sub = (ctx.args[0] || "").toLowerCase();
  if (!["mint", "genesis", "spinup", "new", "create"].includes(sub)) {
    ctx.fail('usage: machine mint "<equation>" [display name]  ·  mints a NEW state machine (new ZID) from an equation', 2);
    return;
  }
  const rest = ctx.args.slice(1).join(" ").trim();
  if (!rest) { ctx.fail('usage: machine mint "<equation>" [display name]  ·  the equation is your identity secret', 2); return; }
  let equation = rest;
  let displayName: string | null = null;
  const m = rest.match(/^["']([^"']+)["']\s*(.*)$/);
  if (m) { equation = m[1]; displayName = (m[2] || "").trim() || null; }
  const HC = await ensureHiteCrypto();
  if (!HC) { ctx.fail("HITE crypto unavailable — `machine mint` runs in the browser CLI.", 1); return; }
  let salt = "";
  let equationHash = "";
  try { salt = HC.randomSaltHex(); equationHash = await HC.hmacHite(equation, salt); }
  catch (e) { ctx.fail("crypto error: " + ((e as Error).message), 1); return; }
  if (!ctx.json) ctx.print("minting state machine · HITE-hashing equation client-side · registering…", "dim");
  const r = await Api.registerV3({ equation_hash: equationHash, equation_salt: salt, display_name: displayName, hash_version: 2 });
  if (!r.ok) { ctx.fail("mint failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
  const d = (r.data || {}) as { zid?: string; token?: string; state_machine?: { slug?: string; api_key?: string } };
  const sm = d.state_machine || {};
  const page = sm.slug ? "/s/" + sm.slug + "/" : null;
  ctx.result.data = { ok: true, zid: d.zid, slug: sm.slug, api_key: sm.api_key, token: d.token, page };
  persistActiveProfile(ctx, { zid: d.zid, slug: sm.slug, api_key: sm.api_key });
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
  ctx.append(card("identity", "state machine minted · " + esc(String(d.zid ?? "—")), [
    '<div class="cli-cko-row"><span class="cli-cko-k">ZID</span><span class="cli-cko-v"><code>' + esc(String(d.zid ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">machine</span><span class="cli-cko-v"><code>' + esc(String(sm.slug ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">api key</span><span class="cli-cko-v"><code>' + esc(String(sm.api_key ?? "—")) + '</code></span></div>',
    page ? '<div class="cli-cko-row"><span class="cli-cko-k">live page</span><span class="cli-cko-v"><code>' + esc(page) + '</code></span></div>' : '',
  ].join(""), '⚠ the equation IS the identity — store it (unrecoverable). api key shown once. This is a NEW machine, separate from your current session.'));
});

// ── Row 12.b — full account lifecycle from the terminal ─────────────────
// `signup` is the one-command genesis: the 7-step wizard derives an account
// equation from the user's phrase (auth-bootstrap), the equation is HITE-
// hashed client-side, register-v3 mints ZID + state machine + api key, and
// (native) the profile is saved. The server NEVER sees the raw equation.
register("signup", async (ctx) => {
  // Verbatim remainder — the phrase may contain anything printable.
  let rest = ctx.raw.replace(/^\s*signup\s*/i, "").replace(/\s+--json\b.*$/i, "").trim();
  let phrase = rest;
  let displayName: string | null = null;
  const m = rest.match(/^["']([^"']+)["']\s*(.*)$/);
  if (m) { phrase = m[1]; displayName = (m[2] || "").trim() || null; }
  if (!phrase) {
    ctx.fail('usage: signup "<a memorable phrase — at least 4 meaningful words>" [display name]\n' +
      'The wizard derives your ACCOUNT EQUATION from the phrase at this exact Zeqond. ' +
      'The equation (not the phrase) becomes your credential — it is shown once.', 2);
    return;
  }
  if (!ctx.json) ctx.print("running the 7-step wizard — deriving your account equation…", "dim");
  const boot = await Api.authBootstrap(phrase);
  if (!boot.ok) { ctx.fail("wizard failed: " + (boot.error || "HTTP " + boot.status), 1, boot.data); return; }
  const bd = (boot.data || {}) as { equation?: string; zeqond?: number; domain?: string; operators?: string[] };
  const equation = (bd.equation || "").trim();
  if (!equation) { ctx.fail("the wizard returned no equation — try again in a moment.", 1); return; }
  const HC = await ensureHiteCrypto();
  if (!HC) { ctx.fail("HITE crypto unavailable.", 1); return; }
  let salt = ""; let equationHash = "";
  try { salt = HC.randomSaltHex(); equationHash = await HC.hmacHite(equation, salt); }
  catch (e) { ctx.fail("crypto error: " + (e as Error).message, 1); return; }
  const r = await Api.registerV3({ equation_hash: equationHash, equation_salt: salt, display_name: displayName, hash_version: 2 });
  if (!r.ok) { ctx.fail("registration failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
  const d = (r.data || {}) as { zid?: string; token?: string; state_machine?: { slug?: string; api_key?: string } };
  const sm = d.state_machine || {};
  const page = sm.slug ? "/s/" + sm.slug + "/" : null;
  persistActiveProfile(ctx, { zid: d.zid, slug: sm.slug, api_key: sm.api_key });
  // Browser: register-v3 set the session cookie — refresh the page identity
  // so the workbench is signed in without a reload (best-effort).
  try {
    const wid = (typeof window !== "undefined" ? (window as unknown as { Zeq?: { identity?: { refresh?: () => Promise<unknown> } } }).Zeq?.identity : null);
    if (wid && typeof wid.refresh === "function") void wid.refresh();
  } catch { /* */ }
  ctx.result.data = { ok: true, zid: d.zid, slug: sm.slug, api_key: sm.api_key, equation, derived_at_zeqond: bd.zeqond, domain: bd.domain, page };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
  ctx.append(card("identity", "identity created · " + esc(String(d.zid ?? "—")), [
    '<div class="cli-cko-row"><span class="cli-cko-k">YOUR ACCOUNT EQUATION</span><span class="cli-cko-v"><code>' + esc(equation) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">ZID</span><span class="cli-cko-v"><code>' + esc(String(d.zid ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">machine</span><span class="cli-cko-v"><code>' + esc(String(sm.slug ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">api key</span><span class="cli-cko-v"><code>' + esc(String(sm.api_key ?? "—")) + '</code></span></div>',
    page ? '<div class="cli-cko-row"><span class="cli-cko-k">live page</span><span class="cli-cko-v"><code>' + esc(page) + '</code></span></div>' : '',
    '<div class="cli-cko-row"><span class="cli-cko-k">derived at</span><span class="cli-cko-v">Zeqond <code>' + esc(String(bd.zeqond ?? "—")) + '</code> · domain <code>' + esc(String(bd.domain ?? "—")) + '</code></span></div>',
  ].join(""),
  '⚠ STORE THE EQUATION EXACTLY — it is your sign-in credential and cannot be recovered. ' +
  'The phrase you typed is NOT the credential (the same phrase at a different Zeqond derives a different equation). ' +
  'The server only ever received the hash. api key shown once. ' +
  'Tip: <code>signup "…" --json &gt; account.json</code> captures everything; later <code>signin ' + esc(String(d.zid ?? "&lt;ZID&gt;")) + ' @equation.txt</code>. ' +
  '→ next: <code>compute NM19 mass=5 acceleration=2</code>'));
});

// `signin` — equation login to an EXISTING identity. Native: the equation is
// read via a hidden prompt (never echoed, never written to history). Web:
// pass it inline — it stays in the page. The equation must be EXACT.
register("signin", async (ctx) => {
  const zid = (ctx.args[0] || "").toUpperCase().trim();
  if (!/^ZEQ\d{11}$/.test(zid)) {
    ctx.fail('usage: signin <ZID> [@equation-file | - | "<equation>"]   · a ZID looks like ZEQ07100437483\n' +
      'Account equations are multi-line — native: @file, `-` (stdin), or omit for a hidden paste prompt.', 2);
    return;
  }
  // Verbatim remainder after the zid token (credential — whitespace matters).
  let equation = "";
  const li = ctx.raw.toLowerCase().indexOf(zid.toLowerCase());
  if (li >= 0) equation = ctx.raw.slice(li + zid.length).replace(/\s+--json\b.*$/i, "").trim();
  const qm = equation.match(/^["'](.+)["']$/s);
  if (qm) equation = qm[1];
  // @file / - : the native entry pre-reads the blob (multi-line credentials).
  if (equation === "-" || equation.startsWith("@")) {
    const pre = (globalThis as unknown as { __zeqSigninEquation?: string }).__zeqSigninEquation;
    if (typeof pre === "string" && pre.trim()) equation = pre.replace(/\s+$/, "");
    else { ctx.fail("could not read the equation from " + (equation === "-" ? "stdin" : equation.slice(1)) + ".", 1); return; }
  }
  if (!equation) {
    const ph = (globalThis as unknown as { __zeqPromptHidden?: (q: string, opts?: { multiline?: boolean }) => Promise<string> }).__zeqPromptHidden;
    if (ph) {
      equation = (await ph(
        "paste the account equation for " + zid + " (input hidden — finish with a single '.' line or Ctrl-D):\n",
        { multiline: true },
      )).replace(/\s+$/, "");
    }
  }
  if (!equation) { ctx.fail("no equation given — signin <ZID> @<file>  ·  zeq signin <ZID> - < equation.txt  ·  or run interactively for a hidden paste prompt.", 2); return; }
  const sr = await Api.saltFor(zid);
  if (!sr.ok) { ctx.fail("salt fetch failed: " + (sr.error || "HTTP " + sr.status), 1); return; }
  const sd = (sr.data || {}) as { salt?: string; hash_version?: number };
  if (!sd.salt) { ctx.fail("no salt in response — try again.", 1); return; }
  if (sd.hash_version === 1) {
    ctx.fail("this is a legacy (v1) identity — sign in once at /auth/ in the browser to upgrade it, then signin here works.", 1);
    return;
  }
  const HC = await ensureHiteCrypto();
  if (!HC) { ctx.fail("HITE crypto unavailable.", 1); return; }
  let candidate = "";
  try { candidate = await HC.hmacHite(equation, sd.salt); }
  catch (e) { ctx.fail("crypto error: " + (e as Error).message, 1); return; }
  const lr = await Api.loginV3(zid, candidate);
  if (!lr.ok) {
    ctx.fail("authentication failed — the equation must match EXACTLY (every space and symbol). " +
      (lr.status === 429 ? "Account temporarily locked — wait and retry." : ""), 1);
    return;
  }
  const ld = (lr.data || {}) as { zid?: string; token?: string; displayName?: string | null; plan?: string };
  // Persist what signin legitimately grants: the identity (zid + session
  // token). It does NOT grant a machine api key — those are shown once at
  // mint; use `profile` to attach one if you have it.
  try {
    if (typeof localStorage !== "undefined" && (globalThis as unknown as { __ZEQ_NATIVE__?: boolean }).__ZEQ_NATIVE__) {
      localStorage.setItem("zeq.cli.profile.zid", zid);
      if (ld.token) localStorage.setItem("zeq.cli.session.token", ld.token);
    }
  } catch { /* */ }
  try {
    const wid = (typeof window !== "undefined" ? (window as unknown as { Zeq?: { identity?: { refresh?: () => Promise<unknown> } } }).Zeq?.identity : null);
    if (wid && typeof wid.refresh === "function") void wid.refresh();
  } catch { /* */ }
  ctx.result.data = { ok: true, zid, displayName: ld.displayName ?? null, plan: ld.plan ?? null };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
  ctx.append(card("identity", "signed in · " + esc(zid), [
    '<div class="cli-cko-row"><span class="cli-cko-k">ZID</span><span class="cli-cko-v"><code>' + esc(zid) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">name</span><span class="cli-cko-v"><code>' + esc(ld.displayName || "—") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">plan</span><span class="cli-cko-v"><code>' + esc(ld.plan || "—") + '</code></span></div>',
  ].join(""), '→ <code>whoami</code> · <code>credits</code> · machine api keys are shown once at mint — <code>profile add</code> attaches one you saved.'));
});

// `sdk` — the SDK linkage card: quickstart snippets wired to YOUR active key
// and THIS origin. Raw key appears only in the user's own terminal (same
// trust model as `keys mint`).
register("sdk", async (ctx) => {
  const origin = (typeof location !== "undefined" && location.origin) ? location.origin : "https://zeq.dev";
  // Resolve the active compute credential the same way authHeaders does.
  let key = "";
  try { if (typeof localStorage !== "undefined") key = localStorage.getItem("zeq.cli.profile.key") || ""; } catch { /* */ }
  if (!key) {
    try {
      const wid = (typeof window !== "undefined" ? (window as unknown as { Zeq?: { identity?: { snapshot?: () => { publishKey?: string; apiKey?: string } } } }).Zeq?.identity : null);
      const snap = wid && typeof wid.snapshot === "function" ? wid.snapshot() : null;
      key = (snap && (snap.publishKey || snap.apiKey)) || "";
    } catch { /* */ }
  }
  const keyShown = key || "<your zeq_ak_… key>";
  const curl =
    "curl -X POST " + origin + "/api/zeq/compute \\\n" +
    "  -H 'Authorization: Bearer " + keyShown + "' \\\n" +
    "  -H 'Content-Type: application/json' \\\n" +
    "  -d '{\"domain\":\"classical-mechanics\",\"operators\":[\"KO42\",\"NM19\"],\"inputs\":{\"mass\":2,\"acceleration\":3}}'";
  const js =
    "const r = await fetch(\"" + origin + "/api/zeq/compute\", {\n" +
    "  method: \"POST\",\n" +
    "  headers: { \"Authorization\": \"Bearer " + keyShown + "\", \"Content-Type\": \"application/json\" },\n" +
    "  body: JSON.stringify({ domain: \"classical-mechanics\", operators: [\"KO42\", \"NM19\"], inputs: { mass: 2, acceleration: 3 } }),\n" +
    "});\nconst envelope = await r.json(); // signed CKO — verify with `zeq verify`";
  const py =
    "# single file, stdlib only — no pip:  curl -fsSO " + origin + "/cli/zeq.py\n" +
    "from zeq import Zeq\n" +
    "z = Zeq(\"" + origin + "\", key=\"" + keyShown + "\")\n" +
    "env = z.compute(\"NM19\", mass=5, acceleration=2)\n" +
    "print(env[\"result\"][\"value\"], z.verify(env)[\"attestation\"][\"verdict\"])  # 10 agree";
  ctx.result.data = { origin, key_present: !!key, curl, js, py, python_client: origin + "/cli/zeq.py", mcp_docs: origin + DOC_MAP["mcp"], kernel_endpoints: KERNEL_ENDPOINTS.map(k => origin + k.path), install: "curl -fsSL " + origin + "/install.sh | sh" };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
  ctx.append(card("identity", "SDK quickstart · " + esc(origin.replace(/^https?:\/\//, "")) + (key ? "" : ' <span class="dim">· no key yet</span>'), [
    (key ? "" : '<div class="cli-cko-row"><span class="cli-cko-k">first</span><span class="cli-cko-v">no active key — <code>signup "&lt;phrase&gt;"</code> creates one, or <code>keys mint my-app write</code> on your machine.</span></div>'),
    '<div class="cli-cko-row"><span class="cli-cko-k">REST</span><span class="cli-cko-v"><pre><code>' + esc(curl) + '</code></pre></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">JavaScript</span><span class="cli-cko-v"><pre><code>' + esc(js) + '</code></pre></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">Python</span><span class="cli-cko-v"><pre><code>' + esc(py) + '</code></pre></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">other languages</span><span class="cli-cko-v">plain HTTPS + JSON (the curl above works from Go/Rust/Java/anything) · or shell out: any <code>zeq … --json</code> returns typed JSON + honest exit codes</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">MCP server</span><span class="cli-cko-v">AI clients (Claude, Cursor, …) speak to the kernel natively — <code>docs mcp</code> · ' + esc(origin + DOC_MAP["mcp"]) + '</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">kernel skill</span><span class="cli-cko-v"><code>' + esc(origin) + '/api/kernel/skill.md</code> — drop into any agent\'s skills directory (<code>docs kernel</code>)</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">native CLI</span><span class="cli-cko-v"><code>' + esc("curl -fsSL " + origin + "/install.sh | sh") + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">docs</span><span class="cli-cko-v"><code>docs api</code> · <code>docs operators</code> · <code>docs auth</code> · <code>docs sdks</code></span></div>',
  ].join(""), '→ every response is a signed CKO envelope — pipe it to <code>verify</code> for the Ed25519 + recompute trust loop.'));
});

// ── Pulse / Zeqond ──────────────────────────────────────────────────────

register("pulse", async (ctx) => {
  const sub = (ctx.args[0] || "").toLowerCase();
  if (sub === "clock") {
    const t = ZeqondClock.now();
    ctx.append(card("pulse", "pulse · live", '<div class="cli-pulse-clock"><div class="cli-pulse-z">Zeqond <code>' + t.zeqond + '</code></div><div class="cli-pulse-phase">phase <code>' + t.phase.toFixed(6) + '</code></div></div>', '1 Zeqond = 0.777 s · re-run `pulse clock` to refresh'));
    return;
  }
  const r = await Api.pulse();
  const t = ZeqondClock.now();
  const d = (r.data || {}) as Record<string, unknown>;
  ctx.result.data = { ok: r.ok, local: { zeqond: t.zeqond, phase: t.phase }, server: r.ok ? d : null };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
  if (r.ok) {
    ctx.append(card("pulse", "pulse · 1.287 Hz", [
      '<div class="cli-cko-row"><span class="cli-cko-k">Zeqond (local)</span><span class="cli-cko-v"><code>' + t.zeqond + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">phase</span><span class="cli-cko-v"><code>' + t.phase.toFixed(6) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">Zeqond (server)</span><span class="cli-cko-v"><code>' + esc(String(d.zeqond ?? "—")) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">frequency</span><span class="cli-cko-v"><code>1.287 Hz</code> <span class="dim">(definitional · τ = 777,000,777 ns)</span></span></div>',
    ].join(""), '→ <code>pulse clock</code> for live · <code>pulse status</code> for machine state'));
  } else {
    ctx.append(card("pulse", "pulse · local only", '<div class="cli-cko-row"><span class="cli-cko-k">Zeqond</span><span class="cli-cko-v"><code>' + t.zeqond + '</code></span></div><div class="cli-cko-row"><span class="cli-cko-k">phase</span><span class="cli-cko-v"><code>' + t.phase.toFixed(6) + '</code></span></div>', 'api-core unreachable · showing local clock'));
  }
});

register("time", (ctx) => {
  const now = new Date();
  const t = ZeqondClock.now();
  ctx.append(card("system", "time", [
    '<div class="cli-cko-row"><span class="cli-cko-k">local</span><span class="cli-cko-v"><code>' + esc(now.toISOString()) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">unix</span><span class="cli-cko-v"><code>' + Math.floor(now.getTime() / 1000) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">Zeqond</span><span class="cli-cko-v"><code>' + t.zeqond + '</code> · phase ' + t.phase.toFixed(6) + '</span></div>',
  ].join("")));
});

// ── Compute & operators ─────────────────────────────────────────────────

register("compute", async (ctx) => {
  // BUILD-D — `--dry-run` (aliases: dry-run, --cost, cost-only) → cost-only
  // preview via GET /api/tally/cost. No execution, no charge, no envelope mint.
  const DRY = /^(--?dry-?run|--?cost(?:-only)?)$/i;
  const dryRun = ctx.args.some(a => DRY.test(a));
  const rest = ctx.args.filter(a => !DRY.test(a));
  const opId = rest[0];
  if (!opId) { ctx.fail("usage: compute <op> [key=value ...] [--dry-run]", 2); return; }
  const inputs: Record<string, number> = {};
  for (const a of rest.slice(1)) {
    const eq = a.indexOf("=");
    if (eq < 0) continue;
    const k = a.slice(0, eq);
    const v = parseFloat(a.slice(eq + 1));
    if (!isFinite(v)) { ctx.fail("bad number for " + k, 2); return; }
    inputs[k] = v;
  }

  if (dryRun) {
    const r = await Api.computeCost([opId]);
    if (!r.ok) { ctx.fail("dry-run failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
    const d = (r.data || {}) as {
      cost?: { total?: number } & Record<string, unknown>;
      price_usd?: string; domains?: string[]; cross_domain?: boolean;
      burn_rate?: number; foundation_rate?: number;
    };
    const total = d.cost?.total;
    const payload = { mode: "dry-run", operator: opId, inputs, charged: false, ...d };
    ctx.result.data = payload;
    if (ctx.json) { ctx.print(JSON.stringify(payload, null, 2), "ok"); return; }
    ctx.append(card("compute", 'dry-run · compute ' + esc(opId) + ' <span class="dim">· no charge</span>', [
      '<div class="cli-cko-row"><span class="cli-cko-k">would cost</span><span class="cli-cko-v"><code>' + esc(typeof total === "number" ? total + " credits" : "—") + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">price</span><span class="cli-cko-v"><code>$' + esc(String(d.price_usd ?? "—")) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">domains</span><span class="cli-cko-v">' + esc((d.domains || []).join(", ") || "—") + (d.cross_domain ? ' <span class="dim">· cross-domain</span>' : '') + '</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">split</span><span class="cli-cko-v"><span class="dim">burn ' + esc(String(d.burn_rate ?? "—")) + ' · foundation ' + esc(String(d.foundation_rate ?? "—")) + '</span></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">inputs</span><span class="cli-cko-v"><code>' + esc(JSON.stringify(inputs)) + '</code></span></div>',
    ].join(""), '→ drop <code>--dry-run</code> to execute · nothing was charged, no envelope minted'));
    return;
  }

  if (!ctx.json) ctx.print("binding " + opId + " · " + JSON.stringify(inputs), "dim");
  // Row-12 audit: without a domain the kernel routes by default solver and
  // dimensional-rejects valid inputs (QM10 frequency=5e14 failed). Infer
  // the domain from the operator prefix so single-op computes route right.
  const PREFIX_DOMAIN: Record<string, string> = {
    QM: "quantum-mechanics", NM: "classical-mechanics", GR: "general-relativity",
    TD: "thermodynamics", SM: "statistical-mechanics", EM: "electromagnetism",
    WP: "wave-physics", FD: "fluid-dynamics", NU: "nuclear-physics",
    OP: "optics", PP: "particle-physics", OM: "orbital-mechanics",
    CS: "computer-science", AC: "acoustics", ZR: "zeq-resonance",
  };
  const prefix = (opId.match(/^[A-Z]+/) || [""])[0];
  const inferredDomain = PREFIX_DOMAIN[prefix];
  const r = await Api.compute([opId], inputs, inferredDomain);
  if (!r.ok) { ctx.fail("compute failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
  // Structured payload — what `--json`, runHeadless, and an embedding app read.
  ctx.result.data = r.data;
  if (ctx.json) { ctx.print(JSON.stringify(r.data, null, 2), "ok"); return; }
  // Phase CI.4 — pass inputs through so the CKO card can render the
  // "push to contract" close-the-loop button, then append learn-more.
  const ckoEl = ckoCard(opId, r.data as ComputeResponse, inputs);
  const lm = renderLearnMore("compute");
  if (lm) {
    const wrap = document.createElement("div");
    wrap.innerHTML = lm;
    const lmEl = wrap.firstElementChild as HTMLElement | null;
    if (lmEl) ckoEl.appendChild(lmEl);
  }
  ctx.append(ckoEl);
});

register("operators", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  if (sub === "count") {
    const r = await Api.operatorsCategories();
    if (!r.ok) { ctx.print("count fetch failed: " + r.error, "err"); return; }
    const d = r.data as { totalOperators?: number; totalDomains?: number; categories?: { domain: string; count?: number }[] };
    const top = (d.categories || []).slice(0, 10);
    ctx.append(card("compute", "operators · " + (d.totalOperators ?? "?") + " total across " + (d.totalDomains ?? "?") + " domains",
      top.map(c => '<div class="cli-cko-row"><span class="cli-cko-k">' + esc(c.domain) + '</span><span class="cli-cko-v"><code>' + esc(String(c.count ?? "?")) + '</code></span></div>').join(""),
      '→ <code>operators list &lt;domain&gt;</code> · <code>operators search &lt;q&gt;</code> · <code>operators show NM19</code>'));
    return;
  }
  if (sub === "categories") {
    const r = await Api.operatorsCategories();
    if (!r.ok) { ctx.print("fetch failed: " + r.error, "err"); return; }
    const d = r.data as { categories?: { domain: string; domainGroup?: string; count?: number }[] };
    const rows = (d.categories || []).map(c => [esc(c.domainGroup ?? "—"), esc(c.domain), '<code>' + esc(String(c.count ?? "?")) + '</code>']);
    ctx.append(card("compute", "operator categories · " + rows.length, "", "→ <code>operators list &lt;domain&gt;</code>"));
    ctx.append(table(["Group", "Domain", "Count"], rows));
    return;
  }
  if (sub === "search") {
    const q = ctx.args.slice(1).join(" ");
    if (!q) { ctx.print("usage: operators search <query>", "err"); return; }
    const r = await Api.operatorsList({ search: q, limit: 25 });
    if (!r.ok) { ctx.fail("search failed: " + (r.error || "HTTP " + r.status), 1); return; }
    const d = r.data as { operators?: { id: string; name: string; domain: string }[]; total?: number; filtered?: number };
    const ops = d.operators || [];
    ctx.result.data = d;
    if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
    if (!ops.length) { ctx.print("no matches for \"" + q + "\"", "dim"); return; }
    ctx.append(card("compute", 'search "' + esc(q) + '" · ' + ops.length + ' / ' + (d.filtered ?? "?"),
      ops.map(o => '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(o.id) + '</code></span><span class="cli-cko-v">' + esc(o.name) + ' <span class="dim">· ' + esc(o.domain) + '</span></span></div>').join(""),
      '→ <code>operators show ' + esc(ops[0].id) + '</code>'));
    return;
  }
  if (sub === "show") {
    const id = ctx.args[1];
    if (!id) { ctx.print("usage: operators show <id>", "err"); return; }
    const r = await Api.operatorsList({ search: id, limit: 5 });
    if (!r.ok) { ctx.fail("fetch failed: " + (r.error || "HTTP " + r.status), 1); return; }
    const d = r.data as { operators?: { id: string; name?: string; domain?: string; description?: string; formula?: string; inputs?: string[]; output?: string }[] };
    const op = (d.operators || []).find(o => o.id.toLowerCase() === id.toLowerCase()) || (d.operators || [])[0];
    if (!op) { ctx.fail("not found: " + id, 1); return; }
    ctx.result.data = op;
    if (ctx.json) { ctx.print(JSON.stringify(op, null, 2), "ok"); return; }
    ctx.append(card("compute", "<code>" + esc(op.id) + "</code> · " + esc(op.name ?? ""), [
      '<div class="cli-cko-row"><span class="cli-cko-k">domain</span><span class="cli-cko-v">' + esc(op.domain ?? "—") + '</span></div>',
      op.formula ? '<div class="cli-cko-row"><span class="cli-cko-k">formula</span><span class="cli-cko-v"><code>' + esc(op.formula) + '</code></span></div>' : '',
      op.description ? '<div class="cli-cko-row"><span class="cli-cko-k">description</span><span class="cli-cko-v">' + esc(op.description) + '</span></div>' : '',
      op.inputs ? '<div class="cli-cko-row"><span class="cli-cko-k">inputs</span><span class="cli-cko-v"><code>' + esc(op.inputs.join(", ")) + '</code></span></div>' : '',
      op.output ? '<div class="cli-cko-row"><span class="cli-cko-k">output</span><span class="cli-cko-v"><code>' + esc(op.output) + '</code></span></div>' : '',
    ].join(""), '→ <code>compute ' + esc(op.id) + ' ' + (op.inputs ? op.inputs.map(k => k + "=…").join(" ") : "k=v") + '</code>'));
    return;
  }
  // default: list
  const dom = ctx.args.slice(1).join(" ");
  const r = await Api.operatorsList({ domain: dom || undefined, limit: 30 });
  if (!r.ok) { ctx.print("list failed: " + r.error, "err"); return; }
  const d = r.data as { operators?: { id: string; name: string; domain: string }[]; filtered?: number };
  const ops = d.operators || [];
  if (!ops.length) { ctx.print("no operators" + (dom ? " in " + dom : ""), "dim"); return; }
  // P21.R13: never print the precise catalogue total on a user surface.
  ctx.append(card("compute", 'operators · ' + ops.length + (dom ? ' of ' + (d.filtered ?? "?") + ' in <code>' + esc(dom) + '</code>' : ' of 1,500+ catalogued (showing first 30)'),
    ops.slice(0, 30).map(o => '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(o.id) + '</code></span><span class="cli-cko-v">' + esc(o.name) + ' <span class="dim">· ' + esc(o.domain) + '</span></span></div>').join(""),
    '→ <code>operators show &lt;id&gt;</code> · <code>compute &lt;id&gt; k=v</code>'));
});

// ── Agents ──────────────────────────────────────────────────────────────

register("agents", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.print("no machine — agents need a machine.", "err"); return; }

  if (sub === "spawn") {
    const directive = ctx.args.slice(1).join(" ").replace(/^["']|["']$/g, "");
    if (!directive) { ctx.print('usage: agents spawn "<directive>"', "err"); return; }
    ctx.print("spawning agent · " + directive, "dim");
    const r = await Api.agentSpawn(directive);
    if (!r.ok) { ctx.fail("spawn failed: " + (r.error || "HTTP " + r.status), 1); return; }
    const d = r.data as { agent?: { zid?: string; id?: string; name?: string; status?: string } };
    const a = d.agent || (r.data as { zid?: string; id?: string; name?: string });
    ctx.result.data = r.data;
    if (ctx.json) { ctx.print(JSON.stringify(r.data, null, 2), "ok"); return; }
    ctx.append(card("agent", "agent spawned", [
      '<div class="cli-cko-row"><span class="cli-cko-k">ZID</span><span class="cli-cko-v"><code>' + esc(String(a.zid ?? a.id ?? "—")) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">name</span><span class="cli-cko-v">' + esc(String((a as { name?: string }).name ?? "—")) + '</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">status</span><span class="cli-cko-v"><code>' + esc(String((a as { status?: string }).status ?? "ticking")) + '</code></span></div>',
    ].join(""), '→ <code>agents tick ' + esc(String(a.zid ?? a.id ?? "")) + '</code> · <code>agents mesh ' + esc(String(a.zid ?? a.id ?? "")) + '</code>'));
    return;
  }
  if (sub === "tick") {
    const id = ctx.args[1];
    if (!id) { ctx.print("usage: agents tick <id>", "err"); return; }
    const r = await Api.agentTick(id);
    if (!r.ok) { ctx.print("tick failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    ctx.append(card("agent", "agent ticked · " + esc(id), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 800) + '</pre>'));
    return;
  }
  if (sub === "kill") {
    const id = ctx.args[1];
    if (!id) { ctx.print("usage: agents kill <id>", "err"); return; }
    const r = await Api.agentKill(id);
    if (!r.ok) { ctx.print("kill failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    ctx.print("agent " + id + " killed.", "ok");
    return;
  }
  if (sub === "mesh") {
    const id = ctx.args[1];
    if (!id) { ctx.print("usage: agents mesh <id>", "err"); return; }
    const r = await Api.agentMesh(id);
    if (!r.ok) { ctx.print("mesh fetch failed: " + r.error, "err"); return; }
    ctx.append(card("agent", "mesh · " + esc(id), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1200) + '</pre>'));
    return;
  }

  // default list
  const r = await Api.agentList(slug);
  if (!r.ok) { ctx.print("list failed: " + (r.error || "HTTP " + r.status), "err"); return; }
  const d = r.data as { agents?: Array<{ id?: string; zid?: string; name?: string; status?: string }> } | Array<{ id?: string; zid?: string; name?: string; status?: string }>;
  const list = Array.isArray(d) ? d : (d.agents || []);
  if (!list.length) { ctx.print("(no agents on this machine yet) · try `agents spawn \"...\"`", "dim"); return; }
  ctx.append(card("agent", "agents · " + list.length,
    list.map(a => '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(String(a.zid ?? a.id ?? "?")) + '</code></span><span class="cli-cko-v">' + esc(a.name ?? "—") + ' <span class="dim">· ' + esc(a.status ?? "?") + '</span></span></div>').join(""),
    '→ <code>agents tick &lt;id&gt;</code> · <code>agents kill &lt;id&gt;</code>'));
});

// ── Skills ──────────────────────────────────────────────────────────────

register("skills", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug ?? "";
  if (sub === "synthesise" || sub === "synthesize") {
    const intent = ctx.args.slice(1).join(" ").replace(/^["']|["']$/g, "");
    if (!intent) { ctx.print('usage: skills synthesise "<intent>"', "err"); return; }
    ctx.print("synthesising skill · " + intent, "dim");
    const r = await Api.skillsSynthesise(intent);
    if (!r.ok) { ctx.print("synthesise failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    ctx.append(card("skill", "skill synthesised", '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1200) + '</pre>'));
    return;
  }
  if (!slug) { ctx.print("no machine — sign in first.", "err"); return; }
  const r = await Api.skillsList(slug);
  if (!r.ok) { ctx.append(card("skill", "skills · <span class=\"dim\">(endpoint pending)</span>", '<div class="dim">' + esc(r.error || "fetch failed") + '</div>')); return; }
  ctx.append(card("skill", "skills", '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1200) + '</pre>',
    '→ <code>skills synthesise "<intent>"</code>'));
});

// ── Contracts ───────────────────────────────────────────────────────────

register("contract", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.print("no machine — sign in.", "err"); return; }

  if (sub === "list") {
    const r = await Api.contractsList(slug);
    if (!r.ok) { ctx.print("list failed: " + r.error, "err"); return; }
    const d = r.data as { contracts?: Array<{ id: string; name?: string; state?: string; next_fire_zeqond?: number; paused_at?: number | null }> };
    const list = d.contracts || (Array.isArray(r.data) ? r.data as Array<{ id: string; name?: string; state?: string; next_fire_zeqond?: number }> : []);
    if (!list.length) { ctx.print("(no contracts) · open the Queries tab to deploy one from a template.", "dim"); return; }
    ctx.append(card("contract", "contracts · " + list.length,
      list.map(c => '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(c.id) + '</code></span><span class="cli-cko-v">' + esc(c.name ?? "—") + ' <span class="dim">· state ' + esc(c.state ?? "?") + ' · next ' + fmtZ(c.next_fire_zeqond as number) + '</span></span></div>').join(""),
      '→ <code>contract show &lt;id&gt;</code> · <code>contract fire-now &lt;id&gt;</code>'));
    return;
  }
  // deploy / import take a JSON contract definition (not an id) — handle first.
  if (sub === "deploy" || sub === "import") {
    const raw = ctx.args.slice(1).join(" ").trim();
    if (!raw) { ctx.print("usage: contract " + sub + " <json>  ·  paste a contract definition (round-trips with `contract export`)", "err"); return; }
    let def: Record<string, unknown>;
    try { def = JSON.parse(raw); }
    catch { ctx.print("invalid JSON — " + sub + " expects a contract-definition object.", "err"); return; }
    ctx.print(sub === "deploy" ? "deploying contract…" : "importing contract…", "dim");
    const r = sub === "deploy" ? await Api.contractDeploy(slug, def) : await Api.contractImport(slug, def);
    if (!r.ok) { ctx.print(sub + " failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    const d = (r.data || {}) as { id?: string; contract?: { id?: string } };
    const cid = String(d.id ?? d.contract?.id ?? "(deployed)");
    ctx.append(card("contract", (sub === "deploy" ? "deployed" : "imported") + " · " + esc(cid),
      '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1400) + '</pre>',
      '→ <code>contract show ' + esc(cid) + '</code> · <code>contract fire-now ' + esc(cid) + '</code>'));
    return;
  }
  const id = ctx.args[1];
  if (!id && sub !== "events") { ctx.print("usage: contract " + sub + " <id>", "err"); return; }
  switch (sub) {
    case "show": {
      const r = await Api.contractGet(slug, id!);
      if (!r.ok) { ctx.print("show failed: " + r.error, "err"); return; }
      ctx.append(card("contract", "contract · " + esc(id!), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1800) + '</pre>'));
      return;
    }
    case "fire-now": {
      const r = await Api.contractFireNow(slug, id!);
      if (!r.ok) { ctx.print("fire-now failed: " + r.error, "err"); return; }
      ctx.append(card("contract", "fired · " + esc(id!), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1200) + '</pre>'));
      return;
    }
    case "pause": {
      const r = await Api.contractPause(slug, id!);
      ctx.print(r.ok ? "paused " + id : ("pause failed: " + r.error), r.ok ? "ok" : "err");
      return;
    }
    case "resume": {
      const r = await Api.contractResume(slug, id!);
      ctx.print(r.ok ? "resumed " + id : ("resume failed: " + r.error), r.ok ? "ok" : "err");
      return;
    }
    case "dry-run": {
      const r = await Api.contractDryRun(slug, id!);
      if (!r.ok) { ctx.print("dry-run failed: " + r.error, "err"); return; }
      ctx.append(card("contract", "dry-run · " + esc(id!), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1800) + '</pre>'));
      return;
    }
    case "preview": {
      const r = await Api.contractPreview(slug, id!);
      if (!r.ok) { ctx.print("preview failed: " + r.error, "err"); return; }
      ctx.append(card("contract", "preview · " + esc(id!), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1800) + '</pre>'));
      return;
    }
    case "next-fires": {
      const r = await Api.contractNextFires(slug, id!, 5);
      if (!r.ok) { ctx.print("next-fires failed: " + r.error, "err"); return; }
      const d = r.data as { fires?: number[] };
      const fires = d.fires || (Array.isArray(r.data) ? r.data as number[] : []);
      ctx.append(card("contract", "next-fires · " + esc(id!),
        fires.length ? fires.map((z, i) => '<div class="cli-cko-row"><span class="cli-cko-k">#' + (i + 1) + '</span><span class="cli-cko-v"><code>' + fmtZ(z) + '</code></span></div>').join("") : '<div class="dim">no upcoming fires</div>'));
      return;
    }
    case "export": {
      const r = await Api.contractExport(slug, id!);
      if (!r.ok) { ctx.print("export failed: " + r.error, "err"); return; }
      ctx.append(card("contract", "exported · " + esc(id!), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1800) + '</pre>',
        'copy/save the JSON · re-import with <code>contract import</code>'));
      return;
    }
    case "events": {
      const r = await Api.events(slug);
      if (!r.ok) { ctx.print("events failed: " + r.error, "err"); return; }
      const d = r.data as { events?: Array<{ id?: string; name?: string; tag?: string; zeqond?: number; payload?: unknown }> };
      const evs = d.events || [];
      ctx.append(card("contract", "events · " + evs.length,
        evs.slice(0, 20).map(e => '<div class="cli-cko-row"><span class="cli-cko-k">' + fmtZ(e.zeqond as number) + '</span><span class="cli-cko-v"><code>' + esc(e.name ?? "—") + '</code> ' + (e.tag ? '<span class="dim">tag=' + esc(e.tag) + '</span>' : "") + '</span></div>').join("") || '<div class="dim">no events</div>'));
      return;
    }
    default:
      ctx.print("unknown contract sub: " + sub, "err");
  }
});

// ── Chain ───────────────────────────────────────────────────────────────

register("state", async (ctx) => {
  const sub = (ctx.args[0] || "audit").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.print("no machine.", "err"); return; }

  if (sub === "head" || sub === "audit") {
    const limit = sub === "head" ? 1 : (parseInt(ctx.args.find(a => /^\d+$/.test(a)) || "10", 10) || 10);
    const r = await Api.chainExplore(slug, limit);
    if (!r.ok) { ctx.fail("state failed: " + (r.error || "HTTP " + r.status), 1); return; }
    const d = (r.data || {}) as { narrative?: Array<{ operator_id?: string; operator_label?: string; operator_category?: string; action?: string }>; rows?: Array<{ zeqondNumber?: string }>; count?: number };
    const nar = d.narrative || [];
    const rws = d.rows || [];
    ctx.result.data = d;
    if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
    const n = d.count ?? nar.length;
    ctx.append(card("chain", "entangled state · " + esc(slug) + " · " + n + " row" + (n === 1 ? "" : "s"),
      nar.length
        ? nar.map((m, i) => {
            const z = rws[i] && rws[i].zeqondNumber != null ? fmtZ(Number(rws[i].zeqondNumber)) : "—";
            return '<div class="cli-cko-row"><span class="cli-cko-k">' + z + '</span><span class="cli-cko-v"><code>' + esc(m.operator_id ?? m.action ?? "—") + '</code> <span class="dim">' + esc(m.operator_label ?? m.operator_category ?? m.action ?? "") + '</span></span></div>';
          }).join("")
        : '<div class="dim">(empty — no transitions yet)</div>',
      '→ <code>state explore</code> for the visual explorer'));
    return;
  }
  if (sub === "explore") {
    if (typeof window !== "undefined") window.open("/state/?slug=" + encodeURIComponent(slug), "_blank", "noopener");
    ctx.print("opened /state/?slug=" + slug, "dim");
    return;
  }
  if (sub === "public") {
    const r = await Api.chainExplore(slug, 10);
    if (!r.ok) { ctx.fail("state failed: " + (r.error || "HTTP " + r.status), 1); return; }
    ctx.result.data = r.data;
    if (ctx.json) { ctx.print(JSON.stringify(r.data, null, 2), "ok"); return; }
    ctx.append(card("chain", "entangled state (public view) · " + esc(slug), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1800) + '</pre>'));
    return;
  }
  if (sub === "verify") { await chainVerifyCmd(ctx); return; }
  ctx.print("usage: state <head|audit|public|explore|verify>", "err");
});
register("chain", REGISTRY["state"]); // back-compat alias (hidden from help/autocomplete)

// ── Row 12.d — the trust trio: conformance · chain verify · doctor ──────

/** SHA-256 hex over a UTF-8 string via WebCrypto (browser AND node 18+). */
async function sha256Hex(s: string): Promise<string> {
  const subtle = (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
  if (!subtle) throw new Error("WebCrypto unavailable");
  const d = await subtle.digest("SHA-256", new TextEncoder().encode(s));
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

// `conformance` — does this LIVE node compute the golden physics, bit for bit?
// 1) fetch the content-addressed corpus (static, /conformance-golden-vectors.json)
//    and verify corpus_sha256 over canonicalJson(vectors) — substituted
//    expectations are caught here;
// 2) ask the node to re-run every vector through its §3 recompute path
//    (GET /api/zeq/conformance — no DB, no clock, no KO42 modulation);
// 3) IEEE-754 bit-compare every value/unit/uncertainty. ALL must match.
register("conformance", async (ctx) => {
  const cr = await Api.raw("GET", "/conformance-golden-vectors.json");
  if (!cr.ok) { ctx.fail("corpus fetch failed: " + (cr.error || "HTTP " + cr.status), 1); return; }
  const corpus = cr.data as { format?: string; corpus_sha256?: string; registry_version?: string; vectors?: Array<{ id: string; expect: { value: number; unit: string; uncertainty: number } }> };
  const vectors = corpus.vectors || [];
  if (corpus.format !== "zeq-golden-vectors-v1" || !vectors.length) { ctx.fail("corpus malformed (format=" + String(corpus.format) + ")", 1); return; }
  let corpusIntact = false;
  try { corpusIntact = (await sha256Hex(cliCanonicalJson(vectors))) === corpus.corpus_sha256; }
  catch { /* WebCrypto missing — reported below */ }
  const rr = await Api.raw("GET", "/api/zeq/conformance");
  if (!rr.ok) { ctx.fail("node conformance run failed: " + (rr.error || "HTTP " + rr.status) + (rr.status === 404 ? " — this node predates /api/zeq/conformance (Row 12.d); update it" : ""), 1); return; }
  const rd = rr.data as { registry_version?: string; corpus_sha256?: string; results?: Array<{ id: string; ok: boolean; value?: number; unit?: string; uncertainty?: number; code?: string }> };
  const results = rd.results || [];
  const byId = new Map(results.map((r) => [r.id, r]));
  const failures: string[] = [];
  for (const v of vectors) {
    const got = byId.get(v.id);
    if (!got || !got.ok) { failures.push(v.id + " — node returned " + (got ? String(got.code) : "nothing")); continue; }
    if (!Object.is(got.value, v.expect.value)) { failures.push(v.id + " — value " + String(got.value) + " ≠ " + String(v.expect.value)); continue; }
    if (got.unit !== v.expect.unit) { failures.push(v.id + " — unit " + String(got.unit) + " ≠ " + v.expect.unit); continue; }
    if (!Object.is(got.uncertainty, v.expect.uncertainty)) { failures.push(v.id + " — uncertainty mismatch"); }
  }
  const pass = corpusIntact && failures.length === 0;
  ctx.result.data = {
    verdict: pass ? "CONFORMANT" : "NOT CONFORMANT",
    corpus_intact: corpusIntact,
    corpus_sha256: corpus.corpus_sha256,
    registry_version: rd.registry_version,
    vectors: vectors.length,
    failures,
  };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), pass ? "ok" : "err"); if (!pass) ctx.result.exitCode = 1; return; }
  ctx.append(card("system", "conformance · " + (pass ? "ZeqVM-COMPATIBLE" : "NOT CONFORMANT"), [
    '<div class="cli-cko-row"><span class="cli-cko-k">corpus integrity</span><span class="cli-cko-v">' + (corpusIntact ? 'content hash VERIFIED <code>' + esc(String(corpus.corpus_sha256).slice(0, 16)) + '…</code>' : '<strong>FAILED — expectations may be substituted</strong>') + '</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">vectors</span><span class="cli-cko-v"><code>' + (vectors.length - failures.length) + ' / ' + vectors.length + '</code> bit-identical (IEEE-754, wire form)</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">registry</span><span class="cli-cko-v"><code>' + esc(String(rd.registry_version ?? "—").slice(0, 16)) + '…</code></span></div>',
    failures.length ? '<div class="cli-cko-row"><span class="cli-cko-k">failures</span><span class="cli-cko-v">' + failures.slice(0, 6).map(esc).join("<br>") + (failures.length > 6 ? '<br>… ' + (failures.length - 6) + ' more' : '') + '</span></div>' : '',
  ].join(""), pass
    ? "a single mutated constant anywhere in this node's physics would fail at least one vector"
    : "do not trust this node's physics until conformance passes"));
  if (!pass) ctx.result.exitCode = 1;
});

// `state verify` / `chain verify` — Proof-of-Elapsed-Zeqonds verification.
// Native: orchestrates the CANONICAL standalone verifier (verify-zeq-chain.mjs
// — VDF spine, elapsed time, binding, links, Ed25519; zero deps, zero
// secrets), downloaded fresh from the origin. Browser: the hash-link leg via
// WebCrypto + an honest pointer to the full native check.
async function chainVerifyCmd(ctx: Ctx): Promise<void> {
  const native = (globalThis as unknown as { __zeqChainVerify?: (origin: string) => Promise<number> }).__zeqChainVerify;
  const origin = (typeof location !== "undefined" && location.origin) ? location.origin : "https://zeq.dev";
  if (native) {
    if (!ctx.json) ctx.print("running the canonical offline verifier (VDF spine · elapsed Zeqonds · binding · links · Ed25519)…", "dim");
    const code = await native(origin);
    ctx.result.data = { verifier: "verify-zeq-chain.mjs", origin, exitCode: code };
    if (code !== 0) ctx.fail("chain verification FAILED (exit " + code + ")", 1);
    else if (!ctx.json) ctx.print("chain verification passed — verdict hash above is deterministic (any verifier prints the same).", "ok");
    return;
  }
  // Browser leg: audit-window hash links from the public seal export.
  const r = await Api.raw("GET", "/api/chain/seal");
  if (!r.ok) { ctx.fail("seal export fetch failed: " + (r.error || "HTTP " + r.status), 1); return; }
  const ex = r.data as { audit_window?: Array<{ zeqond_number: number | string; state_hash: string; prev_hash: string }>; seals?: unknown[] };
  const win = ex.audit_window || [];
  let linksOk = win.length >= 1;
  let why = win.length ? "" : "no audit window in export";
  for (let i = 1; i < win.length; i++) {
    const p = win[i - 1];
    const expected = await sha256Hex(`${p.zeqond_number}|${p.state_hash}|${p.prev_hash}`);
    if (win[i].prev_hash !== expected) { linksOk = false; why = "link broken at window row " + i + " (zeqond " + String(win[i].zeqond_number) + ")"; break; }
  }
  const seals = Array.isArray(ex.seals) ? ex.seals.length : 0;
  ctx.result.data = { leg: "hash-links (browser)", rows: win.length, links_ok: linksOk, seals, full_verify: "native: zeq chain verify · or: node <(curl -fsSL " + origin + "/verify-zeq-chain.mjs) " + origin };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), linksOk ? "ok" : "err"); if (!linksOk) ctx.result.exitCode = 1; return; }
  ctx.append(card("chain", "chain verify · " + (linksOk ? "LINKS INTACT" : "LINKS BROKEN"), [
    '<div class="cli-cko-row"><span class="cli-cko-k">audit window</span><span class="cli-cko-v"><code>' + win.length + '</code> rows · ' + (linksOk ? (win.length - 1) + ' hash links recompute' : esc(why)) + '</span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">VDF seals</span><span class="cli-cko-v"><code>' + seals + '</code> in export <span class="dim">(spine + elapsed-time + Ed25519 legs run in the native verifier)</span></span></div>',
  ].join(""), linksOk
    ? 'this leg proves tamper-EVIDENCE · for tamper-RESISTANCE run the full check: <code>zeq chain verify</code> (native) — VDF spine, elapsed Zeqonds, binding, signatures'
    : 'do not trust this chain'));
  if (!linksOk) ctx.result.exitCode = 1;
}

// `doctor` — one command that tells you what's wrong: origin, clock, auth,
// runtime, update state, conformance reachability. Every ✗ ships a remedy.
register("doctor", async (ctx) => {
  const rows: Array<{ k: string; ok: boolean | null; v: string }> = [];
  const origin = (typeof location !== "undefined" && location.origin) ? location.origin : "—";
  // 1. origin + health
  const h = await Api.raw("GET", "/api/health");
  const hd = (h.data || {}) as { status?: string; git_sha?: string; version?: string };
  rows.push({ k: "origin", ok: h.ok, v: h.ok ? esc(origin) + " · " + esc(hd.status ?? "ok") + (hd.git_sha ? " · " + esc(String(hd.git_sha).slice(0, 8)) : "") : "unreachable (" + (h.status || "network") + ") — check the URL / your connection" });
  // 2. clock — server zeqond vs local canonical floor(ms/777)
  const p = await Api.raw("GET", "/api/zeq/pulse");
  if (p.ok && p.data) {
    const sz = Number((p.data as { zeqond?: number }).zeqond);
    const lz = Math.floor(Date.now() / 777);
    const drift = Math.abs(lz - sz);
    rows.push({ k: "clock", ok: drift <= 2, v: drift <= 2 ? "local Zeqond " + lz + " · server " + sz + " · drift " + drift + " tick" + (drift === 1 ? "" : "s") : "drift " + drift + " Zeqonds — your system clock is off; sync it (timestamps and signatures depend on it)" });
  } else {
    rows.push({ k: "clock", ok: null, v: "pulse unreachable — skipped" });
  }
  // 3. identity / auth
  const id = getIdentity();
  if (id.zid || id.slug) {
    rows.push({ k: "identity", ok: true, v: "<code>" + esc(id.zid ?? "—") + "</code>" + (id.slug ? " · machine <code>" + esc(id.slug) + "</code>" : "") });
    if (id.slug) {
      const kr = await Api.keysList(id.slug);
      rows.push({ k: "auth", ok: kr.ok, v: kr.ok ? "key accepted by this origin" : "key REJECTED (" + (kr.status || "?") + ") — wrong origin for this profile? `profile` lists yours; `signup` mints fresh" });
    }
  } else {
    rows.push({ k: "identity", ok: null, v: "guest — `signup \"<phrase>\"` creates identity + machine + key" });
  }
  // 4. native runtime + update state (self-hash vs the published pin)
  const gNative = (globalThis as unknown as { __ZEQ_NATIVE__?: boolean; __zeqSelfSha256?: () => Promise<string | null>; __zeqNodeVersion?: string }).__ZEQ_NATIVE__;
  if (gNative) {
    const nv = (globalThis as unknown as { __zeqNodeVersion?: string }).__zeqNodeVersion || "";
    rows.push({ k: "runtime", ok: true, v: "native · node " + esc(nv) });
    try {
      const selfSha = await ((globalThis as unknown as { __zeqSelfSha256?: () => Promise<string | null> }).__zeqSelfSha256?.() ?? Promise.resolve(null));
      const pin = await Api.raw("GET", "/cli/zeq.sha256");
      const pinHex = typeof pin.data === "string" ? (pin.data.trim().split(/\s+/)[0] || "") : "";
      if (selfSha && /^[0-9a-f]{64}$/.test(pinHex)) {
        rows.push({ k: "binary", ok: selfSha === pinHex, v: selfSha === pinHex ? "matches the origin's published pin — up to date" : "differs from the origin's pin — `zeq update` (or you run a fork build; fine if intentional)" });
      } else {
        rows.push({ k: "binary", ok: null, v: pin.ok ? "self-hash unavailable" : "origin publishes no pin yet" });
      }
    } catch { rows.push({ k: "binary", ok: null, v: "pin check skipped" }); }
  } else {
    rows.push({ k: "runtime", ok: true, v: "browser (workbench) — native install: <code>curl -fsSL " + esc(origin) + "/install.sh | sh</code>" });
  }
  // 5. conformance surface
  const c = await Api.raw("GET", "/api/zeq/conformance");
  rows.push({ k: "conformance", ok: c.ok ? true : null, v: c.ok ? "surface live — run <code>conformance</code> for the bit-level verdict" : "not on this node yet (predates Row 12.d)" });
  const bad = rows.filter((r) => r.ok === false).length;
  ctx.result.data = { checks: rows.map((r) => ({ check: r.k, ok: r.ok, detail: r.v.replace(/<[^>]+>/g, "") })), problems: bad };
  if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), bad ? "err" : "ok"); if (bad) ctx.result.exitCode = 1; return; }
  ctx.append(card("system", "doctor · " + (bad ? bad + " problem" + (bad === 1 ? "" : "s") : "all clear"),
    rows.map((r) => '<div class="cli-cko-row"><span class="cli-cko-k">' + (r.ok === true ? "✓" : r.ok === false ? "✗" : "·") + " " + esc(r.k) + '</span><span class="cli-cko-v">' + r.v + '</span></div>').join(""),
    bad ? "fix the ✗ items above — each carries its remedy" : "→ <code>conformance</code> · <code>chain verify</code> · <code>tutorial</code>"));
  if (bad) ctx.result.exitCode = 1;
});

// `completion zsh|bash` — emit a completion script for the native shell.
register("completion", (ctx) => {
  const shell = (ctx.args[0] || "").toLowerCase();
  const names = commandNames().join(" ");
  if (shell === "zsh") {
    const script = "#compdef zeq\n_zeq() {\n  local -a cmds\n  cmds=(" + names + ")\n  if (( CURRENT == 2 )); then\n    _describe 'zeq command' cmds\n  fi\n}\n_zeq \"$@\"\n# install: zeq completion zsh > ~/.zeq/_zeq && fpath=(~/.zeq $fpath) && autoload -Uz compinit && compinit";
    ctx.result.data = { shell, script };
    ctx.print(script, "ok");
    return;
  }
  if (shell === "bash") {
    const script = "_zeq_completion() {\n  local cur=\"${COMP_WORDS[COMP_CWORD]}\"\n  if [ \"$COMP_CWORD\" -eq 1 ]; then\n    COMPREPLY=( $(compgen -W \"" + names + "\" -- \"$cur\") )\n  fi\n}\ncomplete -F _zeq_completion zeq\n# install: zeq completion bash >> ~/.bashrc  (or /etc/bash_completion.d/zeq)";
    ctx.result.data = { shell, script };
    ctx.print(script, "ok");
    return;
  }
  ctx.fail("usage: completion <zsh|bash> — prints a shell completion script (the REPL has Tab completion built in)", 2);
});

// ── Site / APIs / PRD ───────────────────────────────────────────────────

register("site", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.print("no machine.", "err"); return; }
  if (sub === "list") {
    const r = await Api.siteList(slug);
    if (!r.ok) { ctx.print("site list failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    const d = (r.data || {}) as { pages?: Array<{ page_slug?: string; pageSlug?: string; title?: string; deploy_zeqond?: number; deployZeqond?: number; url?: string }> };
    const pages = d.pages || [];
    if (!pages.length) {
      ctx.append(card("site", "site · " + esc(slug),
        '<div class="dim">no published pages yet. Build one in the Workbench (step 7) or <code>contract deploy</code> + publish.</div>'));
      return;
    }
    ctx.append(card("site", "published pages · " + pages.length,
      pages.map(p => {
        const ps = p.page_slug ?? p.pageSlug ?? "—";
        const z  = (p.deploy_zeqond ?? p.deployZeqond) as number | undefined;
        return '<div class="cli-cko-row"><span class="cli-cko-k"><code>/' + esc(ps) + '/</code></span><span class="cli-cko-v">' +
          esc(p.title ?? "—") + (typeof z === "number" ? ' <span class="dim">· deployed ' + fmtZ(z) + '</span>' : "") + '</span></div>';
      }).join(""),
      '→ open <code>/s/' + esc(slug) + '/p/&lt;page&gt;/</code> · live on this machine'));
    return;
  }
  if (sub === "deploy") {
    // site deploy <page-slug> <base64-html> [title…]
    // HTML is base64 so it survives the CLI's whitespace tokeniser intact
    // (an app encodes with btoa(html); utf8-safe decode below). This is the
    // same publish path the Workbench step-7 BUILD uses.
    const pageSlug = (ctx.args[1] || "").toLowerCase();
    const b64 = ctx.args[2] || "";
    const title = ctx.args.slice(3).join(" ").trim() || pageSlug;
    if (!pageSlug || !b64) { ctx.fail("usage: site deploy <page-slug> <base64-html> [title]  ·  html = btoa(yourHTML)", 2); return; }
    let html = "";
    try { html = decodeURIComponent(escape(atob(b64))); }
    catch { ctx.fail("invalid base64 html — encode with btoa(html).", 2); return; }
    if (!html) { ctx.fail("decoded html is empty.", 2); return; }
    const w = (typeof window !== "undefined" ? (window as unknown as { Zeq?: { agent?: { zid?: string } } }) : undefined);
    const agentZid = (w?.Zeq?.agent?.zid || getIdentity().zid || "") as string;
    if (!agentZid) { ctx.fail("no identity — sign in first.", 2); return; }
    if (!ctx.json) ctx.print("deploying /s/" + slug + "/p/" + pageSlug + "/ · " + html.length + " bytes…", "dim");
    const r = await Api.sitePublish(slug, pageSlug, { title, html, agentZid });
    if (!r.ok) { ctx.fail("deploy failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
    const p = ((r.data || {}) as { page?: Record<string, unknown> }).page || {};
    const url = "/s/" + slug + "/p/" + pageSlug + "/";
    const payload = { ok: true, url, page: p };
    ctx.result.data = payload;
    if (ctx.json) { ctx.print(JSON.stringify(payload, null, 2), "ok"); return; }
    ctx.append(card("site", "deployed · " + esc(pageSlug) + ' <span class="dim">v' + esc(String(p.version ?? "?")) + '</span>', [
      '<div class="cli-cko-row"><span class="cli-cko-k">live at</span><span class="cli-cko-v"><code>' + esc(url) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">title</span><span class="cli-cko-v">' + esc(title) + '</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">bytes</span><span class="cli-cko-v"><code>' + html.length + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">version</span><span class="cli-cko-v"><code>' + esc(String(p.version ?? "?")) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">proof</span><span class="cli-cko-v"><code>' + esc(String(p.proof_digest ?? p.proofDigest ?? "—").slice(0, 16)) + '…</code></span></div>',
    ].join(""), '→ open <code>' + esc(url) + '</code> · re-run for a new version · <code>site unpublish ' + esc(pageSlug) + '</code> to take down'));
    return;
  }

  if (sub === "get") {
    const pageSlug = (ctx.args[1] || "").toLowerCase();
    if (!pageSlug) { ctx.fail("usage: site get <page-slug>", 2); return; }
    const r = await Api.siteGet(slug, pageSlug);
    if (!r.ok) { ctx.fail("get failed: " + (r.error || "HTTP " + r.status), 1); return; }
    const p = ((r.data || {}) as { page?: { title?: string; html?: string; version?: number } }).page || {};
    ctx.result.data = p;
    if (ctx.json) { ctx.print(JSON.stringify(p, null, 2), "ok"); return; }
    ctx.append(card("site", "page · " + esc(pageSlug) + " v" + esc(String(p.version ?? "?")), [
      '<div class="cli-cko-row"><span class="cli-cko-k">title</span><span class="cli-cko-v">' + esc(p.title ?? "—") + '</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">bytes</span><span class="cli-cko-v"><code>' + (p.html ? p.html.length : 0) + '</code></span></div>',
    ].join("") + '<pre style="max-height:240px;overflow:auto;">' + esc((p.html || "").slice(0, 2000)) + '</pre>',
      '→ live at <code>/s/' + esc(slug) + '/p/' + esc(pageSlug) + '/</code>'));
    return;
  }

  if (sub === "unpublish") {
    const pageSlug = (ctx.args[1] || "").toLowerCase();
    if (!pageSlug) { ctx.fail("usage: site unpublish <page-slug>", 2); return; }
    const r = await Api.siteUnpublish(slug, pageSlug);
    if (!r.ok) { ctx.fail("unpublish failed: " + (r.error || "HTTP " + r.status), 1); return; }
    ctx.result.data = { ok: true, pageSlug, unpublished: true };
    if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
    ctx.print("unpublished " + pageSlug + " · /s/" + slug + "/p/" + pageSlug + "/ is now offline.", "ok");
    return;
  }

  ctx.fail("usage: site <list | deploy <page> <base64-html> [title] | get <page> | unpublish <page>>", 2);
});

register("apis", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.print("no machine.", "err"); return; }
  if (sub === "list") {
    const r = await Api.apisList(slug);
    if (!r.ok) { ctx.append(card("api", "apis · (endpoint pending)", '<div class="dim">' + esc(r.error ?? "fetch failed") + '</div>')); return; }
    const d = r.data as { apis?: Array<{ id: string; name: string; url: string }> } | Array<{ id: string; name: string; url: string }>;
    const list = Array.isArray(d) ? d : (d.apis || []);
    if (!list.length) { ctx.print("(no external APIs registered) · `apis add <name> <url>`", "dim"); return; }
    ctx.append(card("api", "external apis · " + list.length,
      list.map(a => '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(a.id) + '</code></span><span class="cli-cko-v">' + esc(a.name) + ' <span class="dim">· ' + esc(a.url) + '</span></span></div>').join(""),
      '→ <code>apis test &lt;id&gt;</code>'));
    return;
  }
  if (sub === "add") {
    const name = ctx.args[1]; const url = ctx.args[2];
    if (!name || !url) { ctx.print("usage: apis add <name> <url>", "err"); return; }
    const r = await Api.apisAdd(slug, { name, url });
    ctx.print(r.ok ? "added " + name : ("add failed: " + r.error), r.ok ? "ok" : "err");
    return;
  }
  if (sub === "test") {
    const id = ctx.args[1];
    if (!id) { ctx.print("usage: apis test <id>", "err"); return; }
    const r = await Api.apisTest(slug, id);
    if (!r.ok) { ctx.print("test failed: " + r.error, "err"); return; }
    ctx.append(card("api", "test · " + esc(id), '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1200) + '</pre>'));
    return;
  }
  if (sub === "remove") {
    const id = ctx.args[1];
    if (!id) { ctx.print("usage: apis remove <id>", "err"); return; }
    const r = await Api.apisRemove(slug, id);
    ctx.print(r.ok ? "removed " + id : ("remove failed: " + r.error), r.ok ? "ok" : "err");
    return;
  }
});

register("prd", async (ctx) => {
  const sub = (ctx.args[0] || "show").toLowerCase();
  const slug = getSlug() ?? getIdentity().slug;
  if (!slug) { ctx.print("no machine.", "err"); return; }
  if (sub === "show") {
    const r = await Api.prdGet(slug);
    const d = (r.data || {}) as { markdown?: string; content?: string; status?: string; version?: number };
    const md = d.markdown ?? d.content ?? "";
    ctx.result.data = r.ok ? d : { markdown: "", status: "none" };
    if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
    if (!r.ok || !md) { ctx.append(card("site", "prd · " + esc(slug) + " · (empty)", '<div class="dim">no PRD yet — <code>prd edit "&lt;markdown&gt;"</code> to set the build directive.</div>')); return; }
    ctx.append(card("site", "prd · " + esc(slug) + (d.version != null ? " · v" + esc(String(d.version)) : ""), '<pre style="white-space:pre-wrap;">' + esc(md.slice(0, 4000)) + '</pre>', '→ <code>prd edit "..."</code> · <code>prd push</code>'));
    return;
  }
  if (sub === "edit" || sub === "set") {
    const content = ctx.args.slice(1).join(" ").replace(/^["']|["']$/g, "");
    if (!content) { ctx.fail('usage: prd edit "<markdown content>"', 2); return; }
    const r = await Api.prdPut(slug, content);
    if (!r.ok) { ctx.fail("prd edit failed: " + (r.error || "HTTP " + r.status), 1, r.data); return; }
    ctx.result.data = { ok: true, bytes: content.length };
    if (ctx.json) { ctx.print(JSON.stringify(ctx.result.data, null, 2), "ok"); return; }
    ctx.print("PRD updated · " + content.length + " bytes. (`prd show` to view · `prd push` to drive the agent)", "ok");
    return;
  }
  if (sub === "push") {
    ctx.print('PRD push happens from the workbench PRD tab (it dispatches `zeq:prd:push`). Switch tabs to do it.', "dim");
    return;
  }
});

// ── Transparency / health ───────────────────────────────────────────────

register("oracle", async (ctx) => {
  const sub = (ctx.args[0] || "now").toLowerCase();
  if (sub === "history") {
    const n = parseInt(ctx.args[1] || "10", 10) || 10;
    const r = await Api.transparencyHist(n);
    if (!r.ok) { ctx.fail("oracle history failed: " + (r.error || "HTTP " + r.status), 1); return; }
    ctx.result.data = r.data;
    if (ctx.json) { ctx.print(JSON.stringify(r.data, null, 2), "ok"); return; }
    ctx.append(card("system", "transparency oracle · last " + n, '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 1800) + '</pre>'));
    return;
  }
  const r = await Api.transparencyNow();
  if (!r.ok) { ctx.fail("oracle failed: " + (r.error || "HTTP " + r.status), 1); return; }
  const d = (r.data || {}) as Record<string, unknown>;
  ctx.result.data = d;
  if (ctx.json) { ctx.print(JSON.stringify(d, null, 2), "ok"); return; }
  ctx.append(card("system", "transparency oracle · now", [
    '<div class="cli-cko-row"><span class="cli-cko-k">supply</span><span class="cli-cko-v"><code>' + esc(String(d.total_supply ?? d.supply ?? "—")) + ' ZEQ</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">foundation</span><span class="cli-cko-v"><code>' + esc(String(d.foundation_balance ?? d.foundation ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">BTC USD</span><span class="cli-cko-v"><code>' + esc(String(d.btc_usd ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">ETH USD</span><span class="cli-cko-v"><code>' + esc(String(d.eth_usd ?? "—")) + '</code></span></div>',
    '<div class="cli-cko-row"><span class="cli-cko-k">active machines</span><span class="cli-cko-v"><code>' + esc(String(d.active_machines ?? d.machines ?? "—")) + '</code></span></div>',
  ].join(""), '→ <code>oracle history 20</code>'));
});

register("health", async (ctx) => {
  const r = await Api.health();
  if (!r.ok) { ctx.fail("health failed: " + (r.error || "HTTP " + r.status), 1); return; }
  ctx.result.data = r.data;
  if (ctx.json) { ctx.print(JSON.stringify(r.data, null, 2), "ok"); return; }
  ctx.append(card("system", "health · 200 OK", '<pre>' + esc(JSON.stringify(r.data, null, 2)).slice(0, 600) + '</pre>'));
});

register("web", async (ctx) => {
  const url = ctx.args.join(" ");
  if (!url) { ctx.print("usage: web <url>", "err"); return; }
  try {
    const r = await fetch(url, { credentials: "omit" });
    const t = await r.text();
    ctx.append(card("system", "web · " + r.status + " · " + esc(url),
      '<pre style="max-height:300px;overflow:auto;">' + esc(t.slice(0, 4000)) + '</pre>'));
  } catch (e) { ctx.print("fetch failed: " + ((e as Error).message), "err"); }
});

register("theme", (ctx) => {
  const name = (ctx.args[0] || "").toLowerCase();
  if (!name) { ctx.print("themes: cryptopunk · paper · mono. current: " + runtime.theme, "dim"); return; }
  runtime.theme = name;
  const root = document.getElementById("zeq-cli");
  if (root) {
    root.classList.remove("theme-cryptopunk", "theme-paper", "theme-mono");
    root.classList.add("theme-" + name);
  }
  ctx.print("theme → " + name, "ok");
});

// ── Welcome (auto-called on first mount, also typeable) ─────────────────

register("welcome", async (ctx) => {
  ctx.append(renderWelcome());
  const probe = await welcomeProbe();
  if (!probe.healthy) ctx.print("api-core unreachable — commands will degrade to local where possible.", "err");
});

// ── Final touch: zone (legacy) ──────────────────────────────────────────
register("zone", async (ctx) => {
  ctx.print("zone API is now in the Pulse tab — switch tabs to use it.", "dim");
});

// ──────────────────────────────────────────────────────────────────────────
// Phase CI (2026-05-15) — SDK surface inside the CLI.
// `api`, `protocols`, `families`, `skill` so a developer can reach every
// corner of the framework — 138 endpoints, 236 protocols, 1,500+ operators,
// the Kernel Skill — without leaving the terminal. Everything renders as a
// card with try-this hints so it's pure discovery (no AI needed).
// ──────────────────────────────────────────────────────────────────────────

// ── api — the 138-endpoint internal API surface (31 groups) ─────────────
register("api", (ctx) => {
  const sub = (ctx.args[0] || "groups").toLowerCase();

  if (sub === "groups" || sub === "list" && !ctx.args[1]) {
    const rows = API_GROUPS.map(g =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(g.slug) + '</code></span><span class="cli-cko-v">' + esc(g.name) + ' <span class="dim">· ' + g.count + ' endpoints</span><br><span class="dim">' + esc(g.blurb) + '</span></span></div>'
    ).join("");
    ctx.append(card("api", "Zeq API · " + API_TOTAL_ENDPOINTS + " endpoints · " + API_TOTAL_GROUPS + " groups", rows,
      "→ <code>api show &lt;group&gt;</code> opens the SDK page · <code>api search &lt;q&gt;</code> · <code>api total</code>",
      renderLearnMore("api")));
    return;
  }

  if (sub === "total" || sub === "count") {
    ctx.append(card("api", "Zeq API · totals",
      '<div class="cli-cko-row"><span class="cli-cko-k">endpoints</span><span class="cli-cko-v"><code>' + API_TOTAL_ENDPOINTS + '</code></span></div>' +
      '<div class="cli-cko-row"><span class="cli-cko-k">groups</span><span class="cli-cko-v"><code>' + API_TOTAL_GROUPS + '</code></span></div>' +
      '<div class="cli-cko-row"><span class="cli-cko-k">phase-stamped</span><span class="cli-cko-v">every response phase-stamped to the 1.287 Hz HulyaPulse</span></div>',
      "→ <code>api groups</code> for the full list"));
    return;
  }

  if (sub === "show" || sub === "open") {
    const want = (ctx.args[1] || "").toLowerCase();
    if (!want) { ctx.print("usage: api show <group>", "err"); return; }
    const g = API_GROUPS.find(x => x.slug === want || x.name.toLowerCase() === want);
    if (!g) {
      const c = closest(want, API_GROUPS.map(x => x.slug));
      ctx.print('no api group "' + want + '"' + (c ? ' — did you mean `' + c + '`?' : ''), "err");
      return;
    }
    const url = "/sdk/api/" + g.slug + "/";
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
    ctx.print("opened " + url + " (" + g.count + " endpoints)", "dim");
    return;
  }

  if (sub === "search") {
    const q = ctx.args.slice(1).join(" ").toLowerCase();
    if (!q) { ctx.print("usage: api search <query>", "err"); return; }
    const hits = API_GROUPS.filter(g => g.name.toLowerCase().includes(q) || g.slug.includes(q) || g.blurb.toLowerCase().includes(q));
    if (!hits.length) { ctx.print('no matches for "' + q + '"', "dim"); return; }
    ctx.append(card("api", 'api search · "' + esc(q) + '" · ' + hits.length,
      hits.map(g => '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(g.slug) + '</code></span><span class="cli-cko-v">' + esc(g.name) + ' <span class="dim">· ' + g.count + ' endpoints · ' + esc(g.blurb) + '</span></span></div>').join(""),
      "→ <code>api show " + esc(hits[0].slug) + "</code>"));
    return;
  }

  ctx.print("usage: api [groups | total | show <group> | search <q>]", "err");
});

// ── protocols — 236 across 9 themes + 50 domains ─────────────────────────
register("protocols", (ctx) => {
  const sub = (ctx.args[0] || "themes").toLowerCase();

  if (sub === "themes" || sub === "list" && !ctx.args[1]) {
    const rows = PROTOCOL_THEMES.map(t =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>#' + t.index + '</code></span><span class="cli-cko-v">' + esc(t.name) + '<br><span class="dim">' + esc(t.covers) + '</span><br><span class="dim">anchors: ' + t.anchors.map(a => '<code>' + esc(a) + '</code>').join(" · ") + '</span></span></div>'
    ).join("");
    ctx.append(card("system", "Protocols · " + PROTOCOL_TOTAL + " pieces · 9 themes", rows,
      "→ <code>protocols show &lt;n|name&gt;</code> opens the SDK chapter · <code>protocols domains</code>",
      renderLearnMore("protocols")));
    return;
  }

  if (sub === "domains") {
    const rows = PROTOCOL_DOMAINS.map(d =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(d.toLowerCase().replace(/\s+/g, "-")) + '</code></span><span class="cli-cko-v">' + esc(d) + '</span></div>'
    ).join("");
    ctx.append(card("system", "Protocol domains · " + PROTOCOL_DOMAINS.length, rows,
      "→ each domain has its own protocols chapter under <code>/sdk/protocols/&lt;domain&gt;/</code>"));
    return;
  }

  if (sub === "show" || sub === "open") {
    const want = (ctx.args[1] || "").toLowerCase();
    if (!want) { ctx.print("usage: protocols show <number|name>", "err"); return; }
    const t = PROTOCOL_THEMES.find(x =>
      String(x.index) === want ||
      x.name.toLowerCase() === want ||
      x.name.toLowerCase().startsWith(want),
    );
    if (!t) {
      const c = closest(want, PROTOCOL_THEMES.map(x => x.name.toLowerCase()));
      ctx.print('no theme "' + want + '"' + (c ? ' — did you mean `' + c + '`?' : ''), "err");
      return;
    }
    const slug = t.name.toLowerCase().replace(/\s*&\s*/g, "-").replace(/\s+/g, "-");
    const url = "/sdk/protocols/" + slug + "/";
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
    ctx.print("opened theme #" + t.index + " · " + t.name + " · " + url, "dim");
    return;
  }

  if (sub === "search") {
    const q = ctx.args.slice(1).join(" ").toLowerCase();
    if (!q) { ctx.print("usage: protocols search <query>", "err"); return; }
    const inThemes = PROTOCOL_THEMES.filter(t => t.name.toLowerCase().includes(q) || t.covers.toLowerCase().includes(q));
    const inDomains = PROTOCOL_DOMAINS.filter(d => d.toLowerCase().includes(q));
    if (!inThemes.length && !inDomains.length) { ctx.print('no matches for "' + q + '"', "dim"); return; }
    const body =
      (inThemes.length ? '<div class="cli-cko-row"><span class="cli-cko-k">themes</span><span class="cli-cko-v">' + inThemes.map(t => '#' + t.index + ' ' + esc(t.name)).join(" · ") + '</span></div>' : '') +
      (inDomains.length ? '<div class="cli-cko-row"><span class="cli-cko-k">domains</span><span class="cli-cko-v">' + inDomains.map(d => esc(d)).join(" · ") + '</span></div>' : '');
    ctx.append(card("system", 'protocols search · "' + esc(q) + '"', body));
    return;
  }

  ctx.print("usage: protocols [themes | domains | show <name> | search <q>]", "err");
});

// ── families — operator family taxonomy ─────────────────────────────────
register("families", (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();

  if (sub === "list" && !ctx.args[1]) {
    const rows = OPERATOR_FAMILIES.map(f =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(f.slug) + '</code></span><span class="cli-cko-v">' + esc(f.name) + '<br><span class="dim">' + esc(f.range) + '</span><br><span class="dim">' + esc(f.covers) + '</span></span></div>'
    ).join("");
    ctx.append(card("compute", "Operator families · 8 · " + OPERATOR_TOTAL + " operators across " + OPERATOR_DOMAINS + " domains", rows,
      "→ <code>families show &lt;slug&gt;</code> opens the SDK page · <code>operators list &lt;domain&gt;</code> to drill in",
      renderLearnMore("families")));
    return;
  }

  if (sub === "show" || sub === "open" || (sub === "list" && ctx.args[1])) {
    const want = ((sub === "list" ? ctx.args[1] : ctx.args[1]) || "").toLowerCase();
    if (!want) { ctx.print("usage: families show <slug>", "err"); return; }
    const f = OPERATOR_FAMILIES.find(x => x.slug === want || x.name.toLowerCase() === want || x.name.toLowerCase().startsWith(want));
    if (!f) {
      const c = closest(want, OPERATOR_FAMILIES.map(x => x.slug));
      ctx.print('no family "' + want + '"' + (c ? ' — did you mean `' + c + '`?' : ''), "err");
      return;
    }
    const url = "/sdk/operators/" + f.slug + "/";
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
    ctx.print("opened family · " + f.name + " · " + url, "dim");
    return;
  }

  ctx.print("usage: families [list | show <slug>]", "err");
});

// ── skill — the Kernel Skill (zeq-os-mi-kernel) installer + manifest ────
register("skill", async (ctx) => {
  const sub = (ctx.args[0] || "info").toLowerCase();

  if (sub === "info" || sub === "endpoints") {
    const rows = KERNEL_ENDPOINTS.map(e =>
      '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(e.path) + '</code></span><span class="cli-cko-v"><code class="dim">' + esc(e.mime) + '</code><br>' + esc(e.use) + '</span></div>'
    ).join("");
    ctx.append(card("skill", "Kernel Skill · zeq-os-mi-kernel", rows,
      "→ <code>skill prompt</code> · <code>skill md</code> · <code>skill manifest</code> · <code>skill install &lt;client&gt;</code>",
      renderLearnMore("skill")));
    return;
  }

  if (sub === "prompt" || sub === "md" || sub === "manifest") {
    const ep = sub === "prompt" ? "/api/kernel/prompt" :
               sub === "md"     ? "/api/kernel/skill.md" : "/api/kernel/skill";
    try {
      const r = await fetch(ep);
      const txt = await r.text();
      const body = sub === "manifest"
        ? '<pre style="max-height:300px;overflow:auto;">' + esc(txt.slice(0, 4000)) + '</pre>'
        : '<pre style="max-height:400px;overflow:auto;white-space:pre-wrap;">' + esc(txt.slice(0, 6000)) + '</pre>';
      ctx.append(card("skill", "kernel · " + ep + " · " + r.status + " · " + (txt.length / 1024).toFixed(1) + " kB", body,
        "→ truncated to 6 kB · open <code>" + ep + "</code> for the full payload"));
    } catch (e) {
      ctx.print("fetch failed: " + (e as Error).message, "err");
    }
    return;
  }

  if (sub === "install") {
    const client = (ctx.args[1] || "").toLowerCase();
    if (!client) {
      const rows = Object.entries(KERNEL_INSTALLERS).map(([k, v]) =>
        '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(k) + '</code></span><span class="cli-cko-v">' + esc(v.label) + '</span></div>'
      ).join("");
      ctx.append(card("skill", "skill install · pick a client", rows,
        "→ <code>skill install claude-code</code> · <code>cowork</code> · <code>cursor</code> · <code>openai</code>"));
      return;
    }
    const ins = KERNEL_INSTALLERS[client];
    if (!ins) {
      const c = closest(client, Object.keys(KERNEL_INSTALLERS));
      ctx.print('no installer for "' + client + '"' + (c ? ' — did you mean `' + c + '`?' : ''), "err");
      return;
    }
    ctx.append(card("skill", "skill install · " + ins.label,
      '<pre style="white-space:pre;max-width:100%;overflow:auto;">' + esc(ins.snippet) + '</pre>',
      "→ copy the snippet, run it, restart your client"));
    return;
  }

  if (sub === "verify") {
    try {
      const r = await fetch("/api/kernel/skill");
      const j = (await r.json()) as { sha256?: string; version?: string };
      ctx.append(card("skill", "kernel skill · verify",
        '<div class="cli-cko-row"><span class="cli-cko-k">version</span><span class="cli-cko-v"><code>' + esc(String(j.version ?? "?")) + '</code></span></div>' +
        '<div class="cli-cko-row"><span class="cli-cko-k">sha256</span><span class="cli-cko-v"><code>' + esc(String(j.sha256 ?? "?")) + '</code></span></div>',
        "→ recompute locally: <code>curl -sL /api/kernel/skill.md | shasum -a 256</code>"));
    } catch (e) {
      ctx.print("verify failed: " + (e as Error).message, "err");
    }
    return;
  }

  ctx.print("usage: skill [info | prompt | md | manifest | install <client> | verify]", "err");
});

// ── ZSC · Zeq Secure Context (Phase Ω4) ─────────────────────────────────
// Encrypted at-rest secret vault. Calls hit /api/zsc/* which require admin.
// Values are NEVER displayed. set/grant/revoke/rotate/delete all run by
// name, not by value.
register("context", async (ctx) => {
  const sub = (ctx.args[0] || "list").toLowerCase();

  // ── list ──────────────────────────────────────────────────────────────
  if (sub === "list" || sub === "") {
    const r = await Api.zscList();
    if (!r.ok) {
      ctx.print("context list failed: " + (r.error || "HTTP " + r.status) +
        (r.status === 401 ? " — admin session required (sign in at /admin/)" : ""), "err");
      return;
    }
    const data = (r.data || {}) as { count?: number; secrets?: Array<Record<string, unknown>> };
    const secrets = data.secrets || [];
    if (secrets.length === 0) {
      ctx.append(card("system", "context · 0 secrets",
        '<div class="dim">The vault is empty. Try <code>context set MY_KEY my-value</code> to add one.</div>',
        "→ <code>context set &lt;NAME&gt; &lt;VALUE&gt;</code> · values never appear in this terminal once stored."));
      return;
    }
    const rows = secrets.map((s) => {
      const lastReadZ = s.last_read_zeqond as number | null;
      const lastRead = typeof lastReadZ === "number" ? "Z " + lastReadZ : "never";
      const perms = Array.isArray(s.permissions) ? (s.permissions as string[]).length : 0;
      return '<div class="cli-cko-row"><span class="cli-cko-k"><code>' + esc(String(s.name)) +
        '</code></span><span class="cli-cko-v dim">' +
        esc(String(s.bound_zid)) + ' · reads ' + (s.read_count as number) +
        ' · last ' + lastRead + ' · ' + perms + ' grants</span></div>';
    }).join("");
    ctx.append(card("system", "context · " + secrets.length + " secret" + (secrets.length === 1 ? "" : "s"),
      rows,
      "→ <code>context info &lt;NAME&gt;</code> for details · <code>context audit &lt;NAME&gt;</code> for entangled-state rows"));
    return;
  }

  // ── info ──────────────────────────────────────────────────────────────
  if (sub === "info") {
    const name = ctx.args[1];
    if (!name) { ctx.print("usage: context info <NAME>", "err"); return; }
    const r = await Api.zscInfo(name);
    if (!r.ok) {
      ctx.print("context info failed: " + (r.status === 404 ? "no such secret" : (r.error || "HTTP " + r.status)), "err");
      return;
    }
    const s = ((r.data || {}) as { secret?: Record<string, unknown> }).secret || {};
    const perms = Array.isArray(s.permissions) ? (s.permissions as string[]) : [];
    ctx.append(card("system", "context · " + esc(String(s.name)), [
      '<div class="cli-cko-row"><span class="cli-cko-k">id</span><span class="cli-cko-v"><code>' + esc(String(s.id)) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">owner ZID</span><span class="cli-cko-v"><code>' + esc(String(s.bound_zid)) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">purpose</span><span class="cli-cko-v"><code>' + esc(String(s.purpose_tag || "—")) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">read count</span><span class="cli-cko-v"><code>' + (s.read_count as number) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">last read</span><span class="cli-cko-v"><code>' + (s.last_read_zeqond ? "Z " + s.last_read_zeqond : "never") + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">expires</span><span class="cli-cko-v"><code>' + (s.expires_zeqond ? "Z " + s.expires_zeqond : "no auto-rotation") + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">permissions</span><span class="cli-cko-v"><code>' + (perms.length === 0 ? "owner-only" : esc(perms.join(", "))) + '</code></span></div>',
    ].join(""), "→ <code>context audit " + esc(String(s.name)) + "</code> for the entangled-state trail"));
    return;
  }

  // ── set ───────────────────────────────────────────────────────────────
  if (sub === "set") {
    const name = ctx.args[1];
    // Capture everything after the name as the value (allows spaces).
    const valueStart = ctx.raw.indexOf(name);
    const value = valueStart >= 0 ? ctx.raw.slice(valueStart + name.length).trim() : "";
    if (!name || !value) { ctx.print("usage: context set <NAME> <VALUE>", "err"); return; }
    const r = await Api.zscSet(name, value, { purposeTag: "cli_set" });
    if (!r.ok) {
      ctx.print("context set failed: " + (r.error || "HTTP " + r.status), "err");
      return;
    }
    ctx.append(card("system", "context · stored", [
      '<div class="cli-cko-row"><span class="cli-cko-k">name</span><span class="cli-cko-v"><code>' + esc(name) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">encrypted</span><span class="cli-cko-v">AES-256-GCM · PBKDF2-HULYAS key</span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">plaintext on disk</span><span class="cli-cko-v">never</span></div>',
    ].join(""), "→ <code>context info " + esc(name) + "</code> · <code>context audit " + esc(name) + "</code>"));
    return;
  }

  // ── rotate ────────────────────────────────────────────────────────────
  if (sub === "rotate") {
    const name = ctx.args[1];
    if (!name) { ctx.print("usage: context rotate <NAME>", "err"); return; }
    const r = await Api.zscRotate(name);
    if (!r.ok) {
      ctx.print("context rotate failed: " + (r.status === 404 ? "no such secret" : (r.error || "HTTP " + r.status)), "err");
      return;
    }
    ctx.append(card("system", "context · rotated",
      '<div class="cli-cko-row"><span class="cli-cko-k">name</span><span class="cli-cko-v"><code>' + esc(name) + '</code></span></div>' +
      '<div class="cli-cko-row"><span class="cli-cko-k">new IV</span><span class="cli-cko-v">generated · ciphertext refreshed</span></div>' +
      '<div class="cli-cko-row"><span class="cli-cko-k">plaintext</span><span class="cli-cko-v">unchanged</span></div>',
      "→ <code>context audit " + esc(name) + "</code> to see the rotation event"));
    return;
  }

  // ── grant ─────────────────────────────────────────────────────────────
  if (sub === "grant") {
    const name = ctx.args[1];
    const zid = ctx.args[2];
    if (!name || !zid) { ctx.print("usage: context grant <NAME> <ZID>", "err"); return; }
    const r = await Api.zscGrant(name, zid);
    if (!r.ok) { ctx.print("grant failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    const d = (r.data || {}) as { permissions?: string[] };
    ctx.append(card("system", "context · granted", [
      '<div class="cli-cko-row"><span class="cli-cko-k">secret</span><span class="cli-cko-v"><code>' + esc(name) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">added</span><span class="cli-cko-v"><code>' + esc(zid) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">now permitted</span><span class="cli-cko-v"><code>' + esc((d.permissions || []).join(", ") || "—") + '</code></span></div>',
    ].join(""), "Phase Ω4 records grants; Phase Ω6 enforces via contract engine."));
    return;
  }

  // ── revoke ────────────────────────────────────────────────────────────
  if (sub === "revoke") {
    const name = ctx.args[1];
    const zid = ctx.args[2];
    if (!name || !zid) { ctx.print("usage: context revoke <NAME> <ZID>", "err"); return; }
    const r = await Api.zscRevoke(name, zid);
    if (!r.ok) { ctx.print("revoke failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    const d = (r.data || {}) as { permissions?: string[] };
    ctx.append(card("system", "context · revoked", [
      '<div class="cli-cko-row"><span class="cli-cko-k">secret</span><span class="cli-cko-v"><code>' + esc(name) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">removed</span><span class="cli-cko-v"><code>' + esc(zid) + '</code></span></div>',
      '<div class="cli-cko-row"><span class="cli-cko-k">remaining</span><span class="cli-cko-v"><code>' + esc((d.permissions || []).join(", ") || "owner-only") + '</code></span></div>',
    ].join(""), ""));
    return;
  }

  // ── audit ─────────────────────────────────────────────────────────────
  if (sub === "audit") {
    const name = ctx.args[1];
    if (!name) { ctx.print("usage: context audit <NAME> [N]", "err"); return; }
    const limit = Math.min(parseInt(ctx.args[2] || "20", 10) || 20, 200);
    const r = await Api.zscAudit(name, limit);
    if (!r.ok) { ctx.print("audit failed: " + (r.error || "HTTP " + r.status), "err"); return; }
    const d = (r.data || {}) as { count?: number; rows?: Array<Record<string, unknown>> };
    const rows = d.rows || [];
    if (rows.length === 0) {
      ctx.append(card("system", "context audit · " + esc(name),
        '<div class="dim">No audit rows yet — secret has never been read or modified.</div>',
        ""));
      return;
    }
    const body = rows.map((row) => {
      const zn = row.zeqondNumber as string | number;
      const tt = row.transitionType as string;
      const pd = String(row.proofDigest || "");
      return '<div class="cli-cko-row"><span class="cli-cko-k"><code>Z ' + zn + '</code></span>' +
        '<span class="cli-cko-v"><code>' + esc(tt) + '</code> · <span class="dim">' + esc(pd.slice(0, 16)) + '…</span></span></div>';
    }).join("");
    ctx.append(card("system", "context audit · " + esc(name) + " · " + rows.length + " row" + (rows.length === 1 ? "" : "s"),
      body,
      "Each row is hash-linked to the previous. proof_digest = SHA-256(name|zid|transitionId|purpose)."));
    return;
  }

  // ── delete ────────────────────────────────────────────────────────────
  if (sub === "delete" || sub === "rm") {
    const name = ctx.args[1];
    if (!name) { ctx.print("usage: context delete <NAME>", "err"); return; }
    const r = await Api.zscDelete(name);
    if (!r.ok) {
      ctx.print("delete failed: " + (r.status === 404 ? "no such secret" : (r.error || "HTTP " + r.status)), "err");
      return;
    }
    ctx.print("deleted: " + name + " — entangled state retained, plaintext gone.", "ok");
    return;
  }

  ctx.print("usage: context [list | info <NAME> | set <NAME> <VALUE> | rotate <NAME> | grant <NAME> <ZID> | revoke <NAME> <ZID> | audit <NAME> [N] | delete <NAME>]", "err");
});

// ── script — run a sequence of commands (web "script mode", BUILD-G) ─────
// `script <cmd> ; <cmd> ; …` (or newline-separated). Each line runs through
// the headless runner in order; stops on the first failure unless
// `--keep-going` is present. The seam a native CLI's `--script <file>` reuses.
register("script", async (ctx) => {
  const prog = ctx.raw.replace(/^\s*script\s+/i, "").trim();
  if (!prog || prog.toLowerCase() === "help") {
    ctx.print("usage: script <cmd> ; <cmd> ; …   ·  newline- or ';'-separated  ·  stops on first error unless --keep-going", "dim");
    return;
  }
  const keepGoing = /(^|\s)--keep-going(\s|$)/.test(prog);
  const lines = prog.replace(/(^|\s)--keep-going(\s|$)/g, " ").split(/[;\n]+/).map(s => s.trim()).filter(Boolean);
  const runJson = (typeof window !== "undefined")
    ? (window as unknown as { zeqCli?: { runJson?: (c: string) => Promise<{ ok: boolean; exitCode: number; error?: string }> } }).zeqCli?.runJson
    : undefined;
  if (!runJson) { ctx.fail("script: runner unavailable in this context", 1); return; }
  const results: Array<{ line: string; ok: boolean; exitCode: number; error?: string }> = [];
  let failed = 0;
  for (const line of lines) {
    if (/^script(\s|$)/i.test(line)) { ctx.print("  ✗ " + line + "   (nested script not allowed)", "err"); failed++; if (!keepGoing) break; continue; }
    const r = await runJson(line);
    results.push({ line, ok: r.ok, exitCode: r.exitCode, error: r.error });
    if (r.ok) ctx.print("  ✓ " + line, "ok");
    else { ctx.print("  ✗ " + line + "   → " + (r.error || "exit " + r.exitCode), "err"); failed++; if (!keepGoing) break; }
  }
  ctx.result.data = { ran: results.length, total: lines.length, failed, results };
  if (failed) { ctx.result.ok = false; ctx.result.exitCode = 1; }
  ctx.print("script · " + results.length + "/" + lines.length + " run" + (failed ? " · " + failed + " failed" : " · all ok"), failed ? "err" : "ok");
});

// ── profile — named credential profiles (web "config profiles", BUILD-G) ──
// Stores { key, origin } sets in localStorage. `profile use` writes the active
// key to `zeq.cli.profile.key`, which api-client.authHeaders() honours over the
// page identity (same-origin only; cross-origin forks are the native CLI's job).
register("profile", (ctx) => {
  const PROFILES_KEY = "zeq.cli.profiles";
  const ACTIVE_KEY = "zeq.cli.profile.key";
  type Profile = { key: string; origin?: string };
  type Store = { active: string | null; items: Record<string, Profile> };
  const load = (): Store => { try { const r = localStorage.getItem(PROFILES_KEY); if (r) return JSON.parse(r) as Store; } catch { /* */ } return { active: null, items: {} }; };
  const save = (s: Store): void => { try { localStorage.setItem(PROFILES_KEY, JSON.stringify(s)); } catch { /* */ } };
  const mask = (k: string): string => k.length > 12 ? k.slice(0, 8) + "…" + k.slice(-4) : k;
  const here = (typeof location !== "undefined" ? location.origin : "");
  const store = load();
  const sub = (ctx.args[0] || "").toLowerCase();

  if (!sub || sub === "list" || sub === "ls") {
    const names = Object.keys(store.items);
    if (names.length === 0) { ctx.print("no profiles yet · `profile add <name> <key> [origin]` — key = zeq_ak_… or zsm_…", "dim"); return; }
    const body = names.map(n => {
      const p = store.items[n];
      const on = store.active === n;
      return '<div class="cli-cko-row"><span class="cli-cko-k">' + (on ? "● " : "○ ") + esc(n) + '</span>' +
        '<span class="cli-cko-v"><code>' + esc(mask(p.key)) + '</code>' + (p.origin && p.origin !== here ? ' <span class="dim">· ' + esc(p.origin) + '</span>' : '') + '</span></div>';
    }).join("");
    ctx.append(card("identity", "profiles · " + names.length + (store.active ? " · active: " + esc(store.active) : " · none active"), body,
      "`profile use &lt;name&gt;` to switch · `profile clear` for the page identity"));
    ctx.result.data = { active: store.active, profiles: names };
    return;
  }
  if (sub === "add" || sub === "set") {
    const name = ctx.args[1]; const key = ctx.args[2]; const origin = ctx.args[3];
    if (!name || !key) { ctx.fail("usage: profile add <name> <key> [origin]   ·  key = zeq_ak_… or zsm_…", 2); return; }
    store.items[name] = { key, origin: origin || here };
    save(store);
    ctx.print("profile saved: " + name + "  ·  `profile use " + name + "` to activate", "ok");
    return;
  }
  if (sub === "use" || sub === "switch") {
    const name = ctx.args[1];
    if (!name || !store.items[name]) { ctx.fail("no such profile: " + name + "  ·  `profile list`", 1); return; }
    store.active = name; save(store);
    try {
      const it = store.items[name] as Profile & { zid?: string; slug?: string };
      localStorage.setItem(ACTIVE_KEY, it.key);
      if (it.zid) localStorage.setItem("zeq.cli.profile.zid", it.zid); else localStorage.removeItem("zeq.cli.profile.zid");
      if (it.slug) localStorage.setItem("zeq.cli.profile.slug", it.slug); else localStorage.removeItem("zeq.cli.profile.slug");
    } catch { /* */ }
    const o = store.items[name].origin;
    ctx.print("active profile → " + name + (o && o !== here ? "   (origin " + o + " — cross-origin needs the native CLI; same-origin calls use this key)" : ""), "ok");
    return;
  }
  if (sub === "current" || sub === "whoami") {
    ctx.print(store.active ? "active profile: " + store.active : "no active profile — using the page identity", "dim");
    ctx.result.data = { active: store.active };
    return;
  }
  if (sub === "rm" || sub === "remove" || sub === "delete") {
    const name = ctx.args[1];
    if (!name || !store.items[name]) { ctx.fail("no such profile: " + name, 1); return; }
    delete store.items[name];
    if (store.active === name) { store.active = null; try { localStorage.removeItem(ACTIVE_KEY); } catch { /* */ } }
    save(store);
    ctx.print("removed profile: " + name, "ok");
    return;
  }
  if (sub === "clear" || sub === "off") {
    store.active = null; save(store);
    try { localStorage.removeItem(ACTIVE_KEY); } catch { /* */ }
    ctx.print("active profile cleared — back to the page identity", "ok");
    return;
  }
  ctx.fail("usage: profile [list | add <name> <key> [origin] | use <name> | current | rm <name> | clear]", 2);
});

// ── Re-export the runtime for the runner ────────────────────────────────
export function getRegistry(): Record<string, Handler> { return REGISTRY; }
